const previewFormModule = (function () {
  "use strict";

  //#region Parent dependencies
  let _parent;
  let SalesToolkit;
  //#endregion

  //#region Private Variables
  let _eventHandlerMap = null;
  let _ignoredFirstRender = false;
  let _formLoadRequestCounter = 0;
  let _form = null;
  let _viewFormResizeTimeout = 0;
  let _currentWidth = 0;
  let _currentHeight = 0;
  let _tempFileCache = {};
  let _sigPad = null;

  let _submitting = false;
  let _formAPICallInProgress = false;
  let _pendingSubmit = null;
  let _atheneumGL = null;
  let _populatedImage = null;

  let _treeView = null;
  let _treeViewLargeCols = 0;
  let _treeViewMediumCols = 0;
  let _treeViewSmallCols = 0;

  let _cardReader = null;
  let _html5QrCode = null;

  let _populatedFromCard = false;
  let _autoScan = false;

  let _mainTimeoutId = null;
  let _countdownStarterId = null;
  let _countdownIntervalId = null;
  let _pollingIntervalId = null;
  let _remoteCompletionWaiting = false;
  let autoScanTimeout = null;
  let _meetingId = null;

  const _activityEvents = ["mousemove", "mousedown", "touchstart", "keydown"];
  //#endregion

  //#region Private UI & Initialization Functions

  function _showTncsModal() {
    const tncsButton = document.getElementById("salestoolkit-tncs");
    _parent.trackAppClick(
      tncsButton,
      "#salestoolkit-tncs",
      "trackingFormShowTncs",
      SalesToolkit.Translation.get(
        "trackingFormShowTncs",
        "trackingFormShowTncs",
      ),
    );

    const legalHtml = _form.properties?.tncsContent
      ? SalesToolkit.Translation.get(
          _form.properties.tncsContent,
          _form.properties.tncsContent,
        )
      : SalesToolkit.Translation.getOrGetThemeKey("salesToolkitLegalHtml", "");

    const legalTitle = SalesToolkit.Translation.getOrGetThemeKey(
      "salesToolkitLegalTitle",
      "",
    );

    const titleElement = document.getElementById("tncsTitle");
    if (titleElement) {
      titleElement.innerHTML = legalTitle;
    }

    const htmlElement = document.getElementById("legalHtml");
    if (htmlElement) {
      htmlElement.innerHTML = legalHtml;
    }

    _parent.showModal("tncsModal");
  }

  function _askToClearForm() {
    const clearButton = document.getElementById("salestoolkit-clear");
    _parent.trackAppClick(
      clearButton,
      "#salestoolkit-clear",
      "trackingFormClearRequested",
      SalesToolkit.Translation.get(
        "trackingFormClearRequested",
        "trackingFormClearRequested",
      ),
    );

    _parent.showModal("clearFormModal");
  }

  /**
   * Handles window resize events to redraw the form and adjust its height.
   * This is debounced to prevent excessive execution.
   */
  function _resizeHandler() {
    if (SalesToolkit.Theme.getBooleanProperty("scrollTreeView", false)) {
      setTimeout(() => {
        const mainPanel = document.getElementById("main-panel");
        const treeView = document.getElementById("atheneum-tree-view");
        if (!mainPanel || !treeView) return;

        const mainPanelRect = mainPanel.getBoundingClientRect();
        const treeViewRect = treeView.getBoundingClientRect();

        const mainPanelBottom =
          mainPanelRect.top + window.scrollY + mainPanel.offsetHeight;
        let newHeight =
          mainPanelBottom - (treeViewRect.top + window.scrollY) - 12;

        treeView.style.height = `${newHeight}px`;
        treeView.style.overflowY = "scroll";

        const portlet = treeView.closest(".portlet.light");
        if (portlet) {
          portlet.style.paddingBottom = "0";
          portlet.style.marginBottom = "0";
        }
      }, 200);
    }

    _updateScanOverlay();
    // Immediately perform lightweight height adjustment.
    setTimeout(() => _adjustFormHolderHeight(), 0);

    clearTimeout(_viewFormResizeTimeout);
    _viewFormResizeTimeout = setTimeout(() => {
      const formHistory = SalesToolkit.Summary.shares.filter(
        (x) =>
          x.type === "forms" && x.userId === SalesToolkit.User.id && x.editable,
      );
      if (
        SalesToolkit.Theme.getBooleanProperty(
          "showFormHistoryDropdown",
          false,
        ) &&
        _form?.allowSubmissionUpdates &&
        formHistory.length > 0
      ) {
        _buildFormHistoryDropdown();
      }

      // Exit if dimensions haven't changed, with a special check for Android keyboard opening.
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      if (_currentWidth === newWidth && _currentHeight === newHeight) return;
      if (SalesToolkit.CrossPlatform.isAndroid() && _currentWidth === newWidth)
        return;

      _currentWidth = newWidth;
      _currentHeight = newHeight;

      const formHolder = document.getElementById("form-holder");
      if (!formHolder) return;

      _parent.getFormData(formHolder, (formData) => {
        _reloadFormData(formData);
      });
    }, 500);
  }

  function _reloadFormData(formData) {
    const formHolder = document.getElementById("form-holder");
    // 1. Preserve signature data
    let signaturePoints = [];
    let signatureText = "";
    if (_sigPad) {
      signaturePoints = _sigPad.getSignature();
      const sigNameEl = document.querySelector(".atheneum-form-field .sigName");
      if (sigNameEl) {
        signatureText = sigNameEl.value;
      }
    }

    // 2. Preserve file input data in the private cache
    formHolder.querySelectorAll('input[type="file"]').forEach((fileInput) => {
      if (fileInput.files && fileInput.files.length > 0) {
        _tempFileCache[`fileField_${fileInput.name}`] = fileInput.files;
      }
    });

    // 3. Re-render the form, then restore its data
    _parent.renderForm(_form, false, false, formHolder);
    _parent.setFormData(formHolder, formData); // Uses the mediated setFormData function

    // 4. Restore file inputs from the private cache
    formHolder.querySelectorAll('input[type="file"]').forEach((fileInput) => {
      const storedFiles = _tempFileCache[`fileField_${fileInput.name}`];
      if (storedFiles) {
        fileInput.files = storedFiles;
      }
    });

    // 5. Restore signature pad state
    if (_sigPad) {
      const sigNameEl = document.querySelector(".atheneum-form-field .sigName");
      const typedEl = document.querySelector(".atheneum-form-field .typed");
      if (signatureText && sigNameEl && typedEl) {
        sigNameEl.value = signatureText;
        typedEl.innerHTML = signatureText;
      }

      if (signaturePoints && signaturePoints.length > 0) {
        document.querySelector(".signature-wrapper .drawIt a")?.click();
        _sigPad.regenerate(signaturePoints);
      } else if (signatureText) {
        document.querySelector(".signature-wrapper .typeIt a")?.click();
        if (typedEl) typedEl.style.display = "block";
      }
    }

    // 6. Final height adjustment after re-render
    _adjustFormHolderHeight();
  }

  /**
   * Clears user details and session if the form is configured to do so.
   */
  function _clearUserDetailsOnStart() {
    const TRUE_VALUES = ["on", "true", "y", "yes", true];
    if (
      !(
        _form.properties?.clearUserDetailsOnStart &&
        TRUE_VALUES.includes(_form.properties.clearUserDetailsOnStart)
      )
    ) {
      return;
    }

    try {
      SalesToolkit.Cart.assetIds = [];
      SalesToolkit.Cart.collectionIds = [];
      SalesToolkit.Cart.saveToStorage();
      SalesToolkit.Cart.empty(() => SalesToolkit.UI.Cart.updateMenuCartBadge());
      SalesToolkit.UI.Cart.updateMenuCartBadge();
    } catch (err) {
      console.error("Error clearing cart:", err);
    }

    if (typeof SalesToolkit.Forms.userDetails === "object") {
      const message = SalesToolkit.Translation.get(
        "prefillUnloadQRSuccessMessage",
        "Prefill details removed",
      );
      const title = SalesToolkit.Translation.get(
        "prefillUnloadQRSuccessTitle",
        "Goodbye!",
      );
      SalesToolkit.UI.Notifications.showCustomNotification(
        "info",
        message,
        title,
      );
    }

    SalesToolkit.Forms.unloadUserDetails();
    _parent.trackContactSessionEnd(
      SalesToolkit.Translation.get(
        "sessionEndFromFormProperty",
        "sessionEndFromFormProperty",
      ),
    );

    if (
      SalesToolkit.Theme.getBooleanProperty("disableScanningUserQRCodes", true)
    ) {
      document.body.classList.add("userQRScanDisabled");
    }
  }

  //#endregion

  //#region Layout Management
  /**
   * Removes all 'col-*' CSS classes from a given element.
   * @param {HTMLElement} element The element to clean.
   */
  function _removeAllColCSS(element) {
    if (!element) return;
    const classesToRemove = [];
    for (const cls of element.classList) {
      if (cls.startsWith("col-")) {
        classesToRemove.push(cls);
      }
    }
    element.classList.remove(...classesToRemove);
  }

  /**
   * Sets up the responsive grid columns for the main and tree view panels.
   */
  function _setupColumns() {
    const mainPanel = document.getElementById("main-panel");
    const treePanel = document.getElementById("tree-view-panel");
    if (!mainPanel || !treePanel) return;

    _removeAllColCSS(treePanel);
    treePanel.classList.add(`col-lg-${_treeViewLargeCols}`);
    treePanel.classList.add(`col-md-${_treeViewMediumCols}`);
    treePanel.classList.add(`col-sm-${_treeViewSmallCols}`);

    _removeAllColCSS(mainPanel);
    let mainLG = 12;
    let mainMD = 12;
    let mainSM = 12;

    if (!localStorage.getItem("hideTree")) {
      mainLG -= _treeViewLargeCols;
      mainMD -= _treeViewMediumCols;
      mainSM -= _treeViewSmallCols;
    }

    mainPanel.classList.add(`col-lg-${mainLG}`);
    mainPanel.classList.add(`col-md-${mainMD}`);
    mainPanel.classList.add(`col-sm-${mainSM}`);
  }
  //#endregion

  //#region Form Initialization
  function _initForm(form, collectionId) {
    const formNameEl = document.getElementById("salestoolkit-form-name");
    if (formNameEl) {
      let title = SalesToolkit.Translation.get(form.title, form.title);
      if (form.type === SalesToolkit.Forms.Types.CREATE_MEETING && _meetingId) {
        title += SalesToolkit.Translation.get("editingForm", " - Editing");
      }
      formNameEl.innerHTML = title;
    }
    _setupFormButtons(form);
    _clearUserDetailsOnStart();

    const formHolder = document.getElementById("form-holder");
    _parent.renderForm(form, false, false, formHolder);

    _submitting = false;

    if (
      SalesToolkit.Theme.getBooleanProperty("autoSendFromUserDetails", true) &&
      typeof SalesToolkit.Forms.userDetails === "object"
    ) {
      _sendForm();
    }

    _adjustFormHolderHeight();
    _initializeDateFields();

    if (_meetingId && form.type === SalesToolkit.Forms.Types.CREATE_MEETING) {
      SalesToolkit.API.getMeeting(_meetingId, {
        success: function (json) {
          const formHolder = document.getElementById("form-holder");
          _parent.populateFormWithMeetingData(formHolder, json.meetings);
        },
        failure: function (reason, httpStatus, errorCode) {
          // This global seems to be an accepted pattern in the codebase
          Atheneum.Callbacks.logFailureDetails(
            reason,
            httpStatus,
            errorCode,
            "Atheneum.API.getMeeting",
          );
        },
      });
    }
    _renderBreadcrumbs(collectionId);
  }

  function _updateContacts() {
    if (
      SalesToolkit.Theme.getBooleanProperty("updateContactsOnFormLoad", true)
    ) {
      console.log("SPA Navigation: Triggering contacts update for form view.");
      SalesToolkit.Contacts.update({
        success: () => {
          console.log("Contacts updated successfully on form navigation.");
        },
        failure: (reason, httpStatus, errorCode) => {
          console.error("Failed to update contacts on form navigation.", {
            reason,
            httpStatus,
            errorCode,
          });
        },
      });
    }
  }
  /**
   * Sets up the visibility and text of form action buttons based on form properties.
   * @param {object} form - The form configuration object.
   */
  function _setupFormButtons(form) {
    if (!form) return;

    const FALSE_VALUES = ["off", "false", "n", "no", false];

    // Helper to configure a button's visibility and tooltip text
    const configureButton = (
      elementId,
      visibleProperty,
      textProperty,
      defaultTextKey,
      defaultText,
    ) => {
      const element = document.getElementById(elementId);
      if (!element) return;

      const isVisible = !(
        form.properties &&
        FALSE_VALUES.includes(form.properties[visibleProperty])
      );
      element.style.display = isVisible ? "inline-block" : "none";

      if (isVisible) {
        const text =
          (form.properties && form.properties[textProperty]) ||
          SalesToolkit.Translation.get(defaultTextKey, defaultText);
        element.dataset.originalTitle = text;
        element.setAttribute("title", text);
      }
    };

    // Configure Share Link Button
    const isRemoteForm = [
      SalesToolkit.Forms.Types.REMOTE_CONSENT,
      SalesToolkit.Forms.Types.CREATE_MEETING,
      SalesToolkit.Forms.Types.REMOTE,
    ].includes(form.type);
    const shareLinkButton = document.getElementById("salestoolkit-sharelink");
    if (shareLinkButton) {
      if (isRemoteForm) {
        configureButton(
          "salestoolkit-sharelink",
          "shareLinkButtonVisible",
          "shareLinkButton",
          "shareLink",
          "Share",
        );
      } else {
        shareLinkButton.style.display = "none";
      }
    }

    // Configure Submit Button
    configureButton(
      "salestoolkit-submit",
      "submitButtonVisible",
      "submitButton",
      "sendForm",
      "Send",
    );

    // Configure QR Submit Button
    const qrSubmitButton = document.getElementById("salestoolkit-qrsubmit");
    if (qrSubmitButton) {
      const isVisible =
        SalesToolkit.Theme.getBooleanProperty("formQRCodeGeneration", false) &&
        !(
          form.properties &&
          FALSE_VALUES.includes(form.properties.qrSubmitButtonVisible)
        );
      qrSubmitButton.style.display = isVisible ? "inline-block" : "none";
      if (isVisible) {
        const text =
          form.properties?.qrSubmitButton ||
          SalesToolkit.Translation.get("qrSubmit", "Create QR");
        qrSubmitButton.dataset.originalTitle = text;
        qrSubmitButton.setAttribute("title", text);
      }
    }

    // Configure Clear Button
    configureButton(
      "salestoolkit-clear",
      "clearButtonVisible",
      "clearButton",
      "clearForm",
      "Clear Form",
    );

    // Configure Terms and Conditions Button
    const tncsButton = document.getElementById("salestoolkit-tncs");
    if (tncsButton) {
      const hasTncsContent =
        form.properties?.tncsContent ||
        SalesToolkit.Translation.getOrGetThemeKey("salesToolkitLegalHtml", "");
      if (
        hasTncsContent &&
        !(
          form.properties &&
          FALSE_VALUES.includes(form.properties.tncsButtonVisible)
        )
      ) {
        tncsButton.style.display = "inline-block";
        const text =
          form.properties?.tncsButton ||
          SalesToolkit.Translation.get(
            "termsAndConditions",
            "Terms and Conditions",
          );
        tncsButton.dataset.originalTitle = text;
        tncsButton.setAttribute("title", text);
      } else {
        tncsButton.style.display = "none";
      }
    }
  }

  /**
   * Sets the default value for any empty date fields in the form.
   */
  function _initializeDateFields() {
    const formHolder = document.getElementById("form-holder");
    if (!formHolder) return;

    const date = new Date();
    date.setMilliseconds(0);
    date.setSeconds(0);
    date.setMinutes(0);
    date.setHours(date.getHours() + 1);

    const dateInputs = formHolder.querySelectorAll('input[data-type="DATE"]');
    dateInputs.forEach((input) => {
      if (input.value === "") {
        // Set valueAsNumber to correctly handle timezone offset for the input
        input.valueAsNumber = date.getTime() - date.getTimezoneOffset() * 60000;
      }
    });
  }
  /**
   * Performs initial height adjustment of the form container based on viewport and safe areas.
   */
  function _adjustFormHolderHeight() {
    let safeAreaMargins = { top: 0, bottom: 0 };
    if (
      SalesToolkit.CrossPlatform.enabled &&
      !SalesToolkit.CrossPlatform.isAndroid() &&
      window.location.href.includes("index")
    ) {
      try {
        safeAreaMargins = JSON.parse(
          SalesToolkit.CrossPlatform.getSafeAreaMargins(),
        );
      } catch (err) {
        console.error("Error parsing safe area margins:", err);
      }
    }

    const formHolder = document.getElementById("form-holder");
    const portletBody = formHolder.closest(".portlet-body.view-asset");

    if (
      formHolder.offsetHeight <=
      portletBody.offsetHeight + safeAreaMargins.top + safeAreaMargins.bottom
    ) {
      formHolder.style.height = `${
        portletBody.offsetHeight - safeAreaMargins.top - safeAreaMargins.bottom
      }px`;

      const heightAdjust = parseInt(
        formHolder.getAttribute("heightAdjust") || "0",
        10,
      );
      formHolder.style.height = `${formHolder.offsetHeight - heightAdjust}px`;

      if (_ignoredFirstRender) {
        document
          .querySelectorAll(".deleteMePlease")
          .forEach((el) => (el.style.display = "block"));
      } else {
        _ignoredFirstRender = true;
      }
    }
  }

  //#endregion

  //#region Submission

  function _qrSubmit() {
    if (_submitting) return;
    if (_atheneumGL?.apiCallInProgress === true) {
      _pendingSubmit = _qrSubmit;
      return;
    }
    if (_formAPICallInProgress === true) {
      _pendingSubmit = _sendForm;
      return;
    }

    _submitting = true;
    _pendingSubmit = null;

    const qrButton = document.getElementById("salestoolkit-qrsubmit");
    _parent.trackAppClick(
      qrButton,
      "#salestoolkit-qrsubmit",
      "trackingFormQRSubmitRequested",
      SalesToolkit.Translation.get(
        "trackingFormQRSubmitRequested",
        "trackingFormQRSubmitRequested",
      ),
    );

    const callbacks = {
      success: _handleQrSubmitSuccess,
      failure: _handleQrSubmitFailure,
    };

    _parent.submitForm(
      _form,
      document.getElementById("form-holder"),
      callbacks,
      false,
      _populatedImage ?? null,
    );
  }

  /**
   * Generates a QR code from the given form data and displays it in a modal.
   * @param {object} formData The processed form data to encode.
   */
  function _generateAndShowQrCode(formData) {
    const sigFields = _form.fields.filter((x) => x.type === "SIGNATURE");
    sigFields.forEach((sigField) => delete formData[sigField.name]);

     const fileFields = _form.fields.filter((x) => x.type === "FILE");
    fileFields.forEach((fileField) => delete formData[fileField.name]);

    if(SalesToolkit.Theme.getBooleanProperty("stripBase64ImagesFromQRCodeSubmit", true)) {
      const base64Regex = /^data:image\/(png|jpg|jpeg|gif|webp);base64,/;

      Object.keys(formData).forEach((key) => {
        const value = formData[key];
        
        // Check if the value is a string and matches the Base64 pattern
        if (typeof value === "string" && base64Regex.test(value)) {
          delete formData[key];
        }
      });
    }
    
    const excludeList =
      _form.properties?.qrExcludeList ??
      SalesToolkit.Theme.getStringProperty("qrCodeExcludeList", "badgeID,linkedScanImage");
    const excludeEmpty = SalesToolkit.Theme.getBooleanProperty(
      "qrCodeExcludeEmptyFields",
      true,
    );
    const excludeArray = excludeList.split(",");

    for (const key in formData) {
      if (Object.hasOwn(formData, key)) {
        if (
          excludeArray.includes(key) ||
          (excludeEmpty && (formData[key] === "" || formData[key] == null))
        ) {
          delete formData[key];
        }
      }
    }

    const formQRCodeEl = document.getElementById("formQRCode");
    formQRCodeEl.innerHTML = "";
    formData["resourceCentreFormPrefill"] = true;

    const colorHexDark = SalesToolkit.Theme.getColourProperty(
      "prefillQRCodeDarkHex",
      "#000000",
    );
    const colorHexLight = SalesToolkit.Theme.getColourProperty(
      "prefillQRCodeLightHex",
      "#ffffff",
    );
    const errorCorrection = SalesToolkit.Theme.getStringProperty(
      "prefillQRCodeErrorCorrectionLevel",
      "M",
    );

    const qrCode = new QRCode(formQRCodeEl, {
      text: btoa(JSON.stringify(formData)),
      width: SalesToolkit.Theme.getIntegerProperty("prefillQRCodeWidth", 512),
      height: SalesToolkit.Theme.getIntegerProperty("prefillQRCodeHeight", 512),
      colorDark: _parent.colourString(colorHexDark),
      colorLight: _parent.colourString(colorHexLight),
      correctLevel: QRCode.CorrectLevel[errorCorrection],
    });

    console.log("QR code generated:", qrCode);
    _parent.showModal("qrCodeModal");
  }

  /**
   * Handles the success callback for a QR code submission.
   * @param {object} formData The data returned from a successful form submission.
   */
  function _handleQrSubmitSuccess(formData) {
    _populatedImage = undefined;
    _submitting = false;
    let message = SalesToolkit.Translation.get("success", "Success!");
    let title = SalesToolkit.Translation.get("submitted", "Submitted");
    if (_form?.properties) {
      message =
        SalesToolkit.Translation.get(
          _form.properties.submittedMessage,
          _form.properties.submittedMessage,
        ) || message;
      title =
        SalesToolkit.Translation.get(
          _form.properties.submittedTitle,
          _form.properties.submittedTitle,
        ) || title;
    }

    _parent.clearForm(document.getElementById("form-holder"));

    SalesToolkit.Summary.update(() => {
      const formHistory = SalesToolkit.Summary.shares.filter(
        (x) =>
          x.type === "forms" && x.userId === SalesToolkit.User.id && x.editable,
      );
      if (
        SalesToolkit.Theme.getBooleanProperty(
          "showFormHistoryDropdown",
          false,
        ) &&
        _form.allowSubmissionUpdates &&
        formHistory.length > 0
      ) {
        _buildFormHistoryDropdown();
      }
    });

    SalesToolkit.UI.Notifications.showCustomNotification(
      "info",
      message,
      title,
    );
    _generateAndShowQrCode(formData);
  }

  /**
   * Handles the failure callback for a QR code submission.
   */
  function _handleQrSubmitFailure(
    reason,
    httpStatus,
    errorCode,
    formData,
    functionName,
    json,
  ) {
    _populatedImage = undefined;
    _submitting = false;
    let title = SalesToolkit.Translation.get("errorTitle", "Error!");
    if (_form?.properties) {
      title =
        SalesToolkit.Translation.get(
          _form.properties.submittedErrorTitle,
          _form.properties.submittedErrorTitle,
        ) || title;
    }

    let type = "info";
    let message = "";
    let errorCodeHex;
    if (typeof errorCode === "number") {
      errorCodeHex = errorCode.toString(16).padStart(8, "0");
    }

    if (json?.missingParams?.length > 0) {
      type = "system";
      message = SalesToolkit.Translation.getOrGetThemeKey(
        "error" + errorCodeHex,
        SalesToolkit.Translation.get(
          "notificationFormSubmitFailure",
          "Form could not be submitted",
        ),
      );
      if (
        SalesToolkit.Theme.getBooleanProperty(
          "includeErrorCodeForFormSubmission",
          true,
        ) &&
        message === "Form could not be submitted"
      ) {
        message += ` - ${errorCode}`;
      }
      SalesToolkit.UI.Notifications.showCustomNotification(
        type,
        message,
        title,
      );

      json.missingParams.forEach((name) => {
        const field = document.querySelector(`[name="${name}"]`);
        const label = document.querySelector(`label[for="${name}"]`);
        field?.classList.add("invalid", "previously-invalid");
        label?.classList.add("invalid", "previously-invalid");
      });
      return;
    }

    if (Atheneum.Callbacks.isOffline(reason, httpStatus, errorCode)) {
      title = SalesToolkit.Translation.get(
        "offlineSubmissionTitle",
        "Offline Submission",
      );
      message = SalesToolkit.Translation.get(
        "offlineSubmissionMessage",
        "Form submitted while offline, once a connection is established the form will be sent",
      );
      if (_form?.properties) {
        message =
          SalesToolkit.Translation.get(
            _form.properties.submittedMessage,
            _form.properties.submittedMessage,
          ) || message;
        title =
          SalesToolkit.Translation.get(
            _form.properties.submittedOfflineTitle,
            _form.properties.submittedOfflineTitle,
          ) || title;
      }
      SalesToolkit.UI.Notifications.showCustomNotification(
        "info",
        message,
        title,
      );
      _generateAndShowQrCode(formData);
      return;
    }

    if (errorCode === -1086) {
      let key = "notificationFormValidationFailure";
      let fields = _form.fields.filter(
        (field) =>
          !field.required &&
          field.properties?.hasOwnProperty("requiredForNonLink"),
      );
      for (const field of fields) {
        const $field = $('[name="' + field.name + '"]');
        if ($field.length > 0 && !$.trim($field.val())) {
          key =
            "notificationFormValidationFailureWithConditionalRequiredFields";
          break;
        }
      }
      message = SalesToolkit.Translation.get(
        key,
        "Form validation failed, please check each field and try again.",
      );
    } else if (errorCode === -1088) {
      const requiredFields = _form.fields.filter(
        (x) => x.requiredInRemoteForm && x.visible,
      );
      const requiredFieldsLabels = requiredFields
        .map((x) => x.label)
        .join(", ");
      message = SalesToolkit.Translation.get(
        "remoteFormValidationFailed",
        "Some of the fields are required to submit through email, or you can use the share link button. Required fields: {0}",
      ).replace("{0}", requiredFieldsLabels);
    } else if (errorCode === -9999) {
      return; // Error call already handled
    } else {
      const errorKey = `error${errorCodeHex}`;
      const defaultMessageKey =
        ["08071400", "08071706", "08071707", "08071708"].includes(
          errorCodeHex,
        ) ||
        ["08071712", "08071713", "08071714", "08071715"].includes(errorCodeHex)
          ? SalesToolkit.API.ErrorCodes.getName(errorCode)
          : SalesToolkit.Translation.get(
              "notificationFormSubmitFailure",
              "Form could not be submitted",
            );

      message = SalesToolkit.Translation.getOrGetThemeKey(
        errorKey,
        defaultMessageKey,
      );
      type = "system";

      if (
        SalesToolkit.Theme.getBooleanProperty(
          "includeErrorCodeForFormSubmission",
          true,
        ) &&
        message === "Form could not be submitted"
      ) {
        message += ` - ${errorCode}`;
      }
    }
    SalesToolkit.UI.Notifications.showCustomNotification(type, message, title);
  }

  /**
   * Disables the camera modal's primary button and shows a "Processing..." message.
   */
  function _setupProcessing() {
    const primaryButton = document.querySelector(
      "#cameraModal .modal-footer .btn-primary",
    );
    const modalFooter = document.querySelector("#cameraModal .modal-footer");

    if (primaryButton) {
      primaryButton.classList.add("disabled");
      primaryButton.style.pointerEvents = "none";
    }

    if (modalFooter && !document.getElementById("cameraModalProcessing")) {
      const processingSpan = document.createElement("span");
      processingSpan.id = "cameraModalProcessing";
     const isSmallScreen = window.innerHeight <= 750 || window.innerWidth <= 550;

    // Set the position dynamically based on the check
    processingSpan.style.cssText = `
        width: calc(100% - 15px); 
        text-align: center; 
        display: block; 
        height: 34px; 
        line-height: 34px; 
        position: ${isSmallScreen ? 'relative' : 'absolute'};
    `;
      processingSpan.innerHTML = SalesToolkit.Translation.get(
        "cameraModalProcessing",
        "Processing...",
      );
      modalFooter.prepend(processingSpan);
    }
  }

  /**
   * Re-enables the camera modal's primary button and removes the "Processing..." message.
   */
  function _endProcessing() {
    const primaryButton = document.querySelector(
      "#cameraModal .modal-footer .btn-primary",
    );
    const processingSpan = document.getElementById("cameraModalProcessing");

    if (primaryButton) {
      primaryButton.classList.remove("disabled");
      primaryButton.style.pointerEvents = "auto";
    }

    processingSpan?.remove();
  }

  /**
   * Handles the successful submission when 'saveFormDataInSession' is enabled.
   * This preloads the submitted data for subsequent forms.
   * @param {object} formData - The data from the successfully submitted form.
   * @param {string} title - The title for notifications.
   */
  function _handleSaveDataInSession(formData, title) {
    const TRUE_VALUES = ["on", "true", "y", "yes", true];
    if (
      !(
        _form.properties?.saveFormDataInSession &&
        TRUE_VALUES.includes(_form.properties.saveFormDataInSession)
      )
    ) {
      return false; // Indicates that this path was not taken
    }

    // Clean form data before saving to session
    const sigFields = _form.fields.filter((x) => x.type === "SIGNATURE");
    sigFields.forEach((sigField) => delete formData[sigField.name]);

    let excludeList =
      _form.properties.qrExcludeList ??
      SalesToolkit.Theme.getStringProperty("qrCodeExcludeList", "badgeID");
    const excludeEmpty = SalesToolkit.Theme.getBooleanProperty(
      "qrCodeExcludeEmptyFields",
      true,
    );
    const excludeArray = excludeList.split(",");

    for (const key in formData) {
      if (Object.hasOwn(formData, key)) {
        if (
          excludeArray.includes(key) ||
          (excludeEmpty && (formData[key] === "" || formData[key] == null))
        ) {
          delete formData[key];
        }
      }
    }

    formData["resourceCentreFormPrefill"] = true;
    formData["hasConsentStatus"] =
      document.getElementById("form-holder").dataset.contactConsented !==
      undefined;

    if (SalesToolkit.Forms.preloadUserDetails(formData)) {
      _parent.trackContactSessionStart(
        SalesToolkit.Translation.get(
          "sessionStartFromFormSubmission",
          "sessionStartFromFormSubmission",
        ),
      );
      _parent.applyThemeToImage(
        "salestoolkitHeaderIconQRPrefillCancel",
        "#headerPrefillQRCode img.qr-prefill-icon",
        "img/default/Form_QR.svg",
      );

      if (
        SalesToolkit.Theme.getBooleanProperty(
          "dontShowUserDetailsSession",
          true,
        )
      ) {
        document.body.classList.remove("userQRScanDisabled");
      }

      _parent.clearForm(document.getElementById("form-holder"));

      const assetId =
        SalesToolkit.Pages.CurrentPage.getQueryParam("fromAsset") || "";
      const notificationMessage = SalesToolkit.Translation.get(
        "prefillSubmitSuccess",
        "These details have been preloaded for future form submissions.",
      );

      if (assetId) {
        let urlParams = _getUrlVars();
        delete urlParams.fromAsset;
        localStorage.setItem(
          "notificationsToShow",
          JSON.stringify([
            { body: notificationMessage, title: title, type: "info" },
          ]),
        );
        _parent.navigator.loadAssetScreen(assetId, urlParams);
      } else if (
        SalesToolkit.Theme.getBooleanProperty("closeFormsOnSubmit", false)
      ) {
        localStorage.setItem(
          "notificationsToShow",
          JSON.stringify([
            { body: notificationMessage, title: title, type: "info" },
          ]),
        );
        _backButtonHome();
      } else {
        SalesToolkit.UI.Notifications.showCustomNotification(
          "info",
          notificationMessage,
          title,
        );
      }
      return true; // Indicates this path was taken and has concluded
    }
    return false;
  }

  /**
   * Handles the success callback for a standard form submission.
   * @param {object} formData - The data returned from a successful form submission.
   */
  async function _handleSendFormSuccess(formData) {
    _populatedImage = null;
    _submitting = false;
    let message = SalesToolkit.Translation.get("success", "Success!");
    let title = SalesToolkit.Translation.get("submitted", "Submitted");
    if (_form?.properties) {
      message =
        SalesToolkit.Translation.get(
          _form.properties.submittedMessage,
          _form.properties.submittedMessage,
        ) || message;
      title =
        SalesToolkit.Translation.get(
          _form.properties.submittedTitle,
          _form.properties.submittedTitle,
        ) || title;
    }

    SalesToolkit.Summary.update(() => {
      const formHistory = SalesToolkit.Summary.shares.filter(
        (x) =>
          x.type === "forms" && x.userId === SalesToolkit.User.id && x.editable,
      );
      if (
        SalesToolkit.Theme.getBooleanProperty(
          "showFormHistoryDropdown",
          false,
        ) &&
        _form.allowSubmissionUpdates &&
        formHistory.length > 0
      ) {
        _buildFormHistoryDropdown();
      }
    });

    // Attempt to handle the "save data in session" flow. If it completes, we stop here.
    if (_handleSaveDataInSession(formData, title)) {
      return;
    }

    // Standard success flow
    _parent.clearForm(document.getElementById("form-holder"));
    if (SalesToolkit.Theme.getBooleanProperty("closeFormsOnSubmit", false)) {
      localStorage.setItem(
        "notificationsToShow",
        JSON.stringify([{ body: message, title: title, type: "info" }]),
      );
      _backButtonHome();
      return;
    }

    SalesToolkit.UI.Notifications.showCustomNotification(
      "info",
      message,
      title,
    );
  }

  async function _handleSendFormFailure(
    reason,
    httpStatus,
    errorCode,
    formData,
    functionName,
    json,
  ) {
    _populatedImage = null;
    _submitting = false;
    const TRUE_VALUES = ["on", "true", "y", "yes", true];

    let title = SalesToolkit.Translation.get("errorTitle", "Error!");
    if (_form?.properties) {
      title =
        SalesToolkit.Translation.get(
          _form.properties.submittedErrorTitle,
          _form.properties.submittedErrorTitle,
        ) || title;
    }

    let type = "info";
    let message = "";
    let errorCodeHex;
    if (typeof errorCode === "number") {
      errorCodeHex = errorCode.toString(16).padStart(8, "0");
    }

    if (json?.missingParams?.length > 0) {
      type = "system";
      message = SalesToolkit.Translation.getOrGetThemeKey(
        "error" + errorCodeHex,
        SalesToolkit.Translation.get(
          "notificationFormSubmitFailure",
          "Form could not be submitted",
        ),
      );
      if (
        SalesToolkit.Theme.getBooleanProperty(
          "includeErrorCodeForFormSubmission",
          true,
        ) &&
        message === "Form could not be submitted"
      ) {
        message += ` - ${errorCode}`;
      }
      SalesToolkit.UI.Notifications.showCustomNotification(
        type,
        message,
        title,
      );

      json.missingParams.forEach((name) => {
        const field = document.querySelector(`[name="${name}"]`);
        const label = document.querySelector(`label[for="${name}"]`);
        field?.classList.add("invalid", "previously-invalid");
        label?.classList.add("invalid", "previously-invalid");
      });
      return;
    }

    if (Atheneum.Callbacks.isOffline(reason, httpStatus, errorCode)) {
      title = SalesToolkit.Translation.get(
        "offlineSubmissionTitle",
        "Offline Submission",
      );
      message = SalesToolkit.Translation.get(
        "offlineSubmissionMessage",
        "Form submitted while offline, once a connection is established the form will be sent",
      );
      if (_form?.properties) {
        message =
          SalesToolkit.Translation.get(
            _form.properties.submittedMessage,
            _form.properties.submittedMessage,
          ) || message;
        title =
          SalesToolkit.Translation.get(
            _form.properties.submittedOfflineTitle,
            _form.properties.submittedOfflineTitle,
          ) || title;
      }

      if (
        _form.properties?.saveFormDataInSession &&
        TRUE_VALUES.includes(_form.properties.saveFormDataInSession)
      ) {
        formData = _cleanFormDataForSession(formData);
        formData["resourceCentreFormPrefill"] = true;

        SalesToolkit.Session.sessionId = "offline-" + _uuid();
        SalesToolkit.Session.saveToStorage();

        if (SalesToolkit.Forms.preloadUserDetails(formData)) {
          _parent.trackContactSessionStart(
            SalesToolkit.Translation.get(
              "sessionStartFromOfflineFormSubmission",
              "sessionStartFromOfflineFormSubmission",
            ),
          );
          _parent.applyThemeToImage(
            "salestoolkitHeaderIconQRPrefillCancel",
            "#headerPrefillQRCode img.qr-prefill-icon",
            "img/default/Form_QR.svg",
          );

          if (
            SalesToolkit.Theme.getBooleanProperty(
              "dontShowUserDetailsSession",
              true,
            )
          ) {
            document.body.classList.remove("userQRScanDisabled");
          }

          _parent.clearForm(document.getElementById("form-holder"));

          const successMessage = SalesToolkit.Translation.get(
            "prefillSubmitSuccess",
            "These details have been preloaded for future form submissions.",
          );
          if (
            SalesToolkit.Theme.getBooleanProperty("closeFormsOnSubmit", false)
          ) {
            localStorage.setItem(
              "notificationsToShow",
              JSON.stringify([
                { body: message, title: title, type: "info" },
                { body: successMessage, title: title, type: "cart" },
              ]),
            );
            _backButtonHome();
          } else {
            SalesToolkit.UI.Notifications.showCustomNotification(
              "info",
              successMessage,
              title,
            );
          }
          return;
        }
      } else {
        _parent.clearForm(document.getElementById("form-holder"));
        if (
          SalesToolkit.Theme.getBooleanProperty("closeFormsOnSubmit", false)
        ) {
          localStorage.setItem(
            "notificationsToShow",
            JSON.stringify([{ body: message, title: title, type: "info" }]),
          );
          _backButtonHome();
          return;
        }
      }
    } else if (errorCode === -1086) {
      let key = "notificationFormValidationFailure";
      let fields = _form.fields.filter(
        (field) =>
          !field.required &&
          field.properties?.hasOwnProperty("requiredForNonLink"),
      );
      for (const field of fields) {
        const $field = $('[name="' + field.name + '"]');
        if ($field.length > 0 && !$.trim($field.val())) {
          key =
            "notificationFormValidationFailureWithConditionalRequiredFields";
          break;
        }
      }
      message = SalesToolkit.Translation.getOrGetThemeKey(
        key,
        "Form validation failed, please check each field and try again.",
      );
    } else if (errorCode === -1088) {
      const requiredFields = _form.fields.filter(
        (x) => x.requiredInRemoteForm && x.visible,
      );
      const requiredFieldsLabels = requiredFields
        .map((x) => x.label)
        .join(", ");
      message = SalesToolkit.Translation.get(
        "remoteFormValidationFailed",
        "Some of the fields are required to submit through email, or you can use the share link button. Required fields: {0}",
      ).replace("{0}", requiredFieldsLabels);
    } else if (errorCode === -9999) {
      return; // Error call already handled
    } else {
      const errorKey = `error${errorCodeHex}`;
      const defaultMessageKey =
        ["08071400", "08071706", "08071707", "08071708"].includes(
          errorCodeHex,
        ) ||
        ["08071712", "08071713", "08071714", "08071715"].includes(errorCodeHex)
          ? SalesToolkit.API.ErrorCodes.getName(errorCode)
          : SalesToolkit.Translation.get(
              "notificationFormSubmitFailure",
              "Form could not be submitted",
            );

      message = SalesToolkit.Translation.getOrGetThemeKey(
        errorKey,
        defaultMessageKey,
      );
      type = "system";

      if (
        SalesToolkit.Theme.getBooleanProperty(
          "includeErrorCodeForFormSubmission",
          true,
        ) &&
        message === "Form could not be submitted"
      ) {
        message += ` - ${errorCode}`;
      }
    }
    SalesToolkit.UI.Notifications.showCustomNotification(type, message, title);
  }

  /**
   * Initiates the remote completion flow by generating a QR code for the user to scan.
   */
  function _handleRemoteCompletion() {
    const formHolder = document.getElementById("form-holder");
    if (_parent.validateForm(formHolder)) {
      _parent.getFormData(formHolder, function (fields) {
        const form = _form;
        SalesToolkit.Cart.shareRemoteCompletion(form, fields, {
          success: function (json) {
            const qrCodeEl = document.getElementById("formQRCode");
            qrCodeEl.innerHTML = "";

            const colorHexDark = SalesToolkit.Theme.getColourProperty(
              "externalQRCodeDarkHex",
              "#000000",
            );
            const colorHexLight = SalesToolkit.Theme.getColourProperty(
              "externalQRCodeLightHex",
              "#ffffff",
            );
            const errorCorrection = SalesToolkit.Theme.getStringProperty(
              "externalQRCodeErrorCorrectionLevel",
              "M",
            );

            const qrCode = new QRCode(qrCodeEl, {
              text: json.link,
              width: SalesToolkit.Theme.getIntegerProperty(
                "externalQRCodeWidth",
                512,
              ),
              height: SalesToolkit.Theme.getIntegerProperty(
                "externalQRCodeHeight",
                512,
              ),
              colorDark: _parent.colourString(colorHexDark),
              colorLight: _parent.colourString(colorHexLight),
              correctLevel: QRCode.CorrectLevel[errorCorrection],
            });

            console.log("QR code generated:", qrCode);

            const notificationBody = SalesToolkit.Translation.get(
              "notificationCartShareRemoteCompletionSuccess",
              "Cart contents sent successfully",
            );
            SalesToolkit.UI.Notifications.showCustomNotification(
              "info",
              notificationBody,
            );

            const closeButton = document.querySelector("#qrCodeModal button");
            if (closeButton) {
              closeButton.style.display = SalesToolkit.Theme.getBooleanProperty(
                "showRemoteCompletionCloseButton",
                true,
              )
                ? "block"
                : "none";
            }

            _parent.showModal("qrCodeModal");
            _startRemoteCompletion(json.id);
          },
          failure: function (reason, httpStatus, errorCode) {
            let errorCodeHex;
            if (typeof errorCode === "number") {
              errorCodeHex = errorCode.toString(16).padStart(8, "0");
            }

            if (Atheneum.Callbacks.isOffline(reason, httpStatus, errorCode)) {
              const notificationBody = SalesToolkit.Translation.getFormatted(
                "notificationCartRemoteCompletionShareOffline",
                "Remote completion can only be completed while online. Please check your connection and try again.",
              );
              SalesToolkit.UI.Notifications.showCustomNotification(
                "info",
                notificationBody,
              );
            } else {
              const notificationBody = SalesToolkit.Translation.get(
                "notificationCartRemoteShareFailure",
                SalesToolkit.Translation.getOrGetThemeKey(
                  "error" + errorCodeHex,
                  "Remote completion could not be completed.",
                ),
              );
              SalesToolkit.UI.Notifications.showCustomNotification(
                "info",
                notificationBody,
              );
            }
          },
        });
      });
    }
  }

  function _sendForm() {
    if (_submitting) return;
    if (_atheneumGL?.apiCallInProgress === true) {
      _pendingSubmit = _sendForm;
      return;
    }
    if (_formAPICallInProgress === true) {
      _pendingSubmit = _sendForm;
      return;
    }

    _submitting = true;
    _pendingSubmit = null;

    const submitButton = document.getElementById("salestoolkit-submit");
    _parent.trackAppClick(
      submitButton,
      "#salestoolkit-submit",
      "trackingFormSubmitRequested",
      SalesToolkit.Translation.get(
        "trackingFormSubmitRequested",
        "trackingFormSubmitRequested",
      ),
    );

    const callbacks = {
      success: _handleSendFormSuccess,
      failure: _handleSendFormFailure,
    };

    const formHolder = document.getElementById("form-holder");
    const emailField = _form?.fields.find((x) => x.type === "EMAIL");
    let hasEmail = false;

    if (emailField) {
      const emailElement = formHolder.querySelector(
        `[name="${emailField.name}"]`,
      );
      if (emailElement) {
        hasEmail = emailElement.value.trim().length > 0;
      }
    }

    const shouldRemoteComplete =
      _form?.properties?.remoteCompletionRequired === "true" ||
      (!hasEmail && _form?.properties?.remoteCompletionEnabled === "true");

    if (shouldRemoteComplete) {
      _handleRemoteCompletion();
    } else {
      _parent.submitForm(
        _form,
        formHolder,
        callbacks,
        false,
        _populatedImage ?? null,
      );
    }
  }

  /**
   * Cleans form data by removing signature fields and applying exclusion rules.
   * @param {object} formData The form data to clean.
   * @returns {object} The cleaned form data.
   */
  function _cleanFormDataForSession(formData) {
    const sigFields = _form.fields.filter((x) => x.type === "SIGNATURE");
    sigFields.forEach((sigField) => delete formData[sigField.name]);

    const excludeList =
      _form.properties?.qrExcludeList ??
      SalesToolkit.Theme.getStringProperty("qrCodeExcludeList", "badgeID");
    const excludeEmpty = SalesToolkit.Theme.getBooleanProperty(
      "qrCodeExcludeEmptyFields",
      true,
    );
    const excludeArray = excludeList.split(",");

    for (const key in formData) {
      if (Object.hasOwn(formData, key)) {
        if (
          excludeArray.includes(key) ||
          (excludeEmpty && (formData[key] === "" || formData[key] == null))
        ) {
          delete formData[key];
        }
      }
    }
    return formData;
  }
  //#endregion

  //#region ShareLink
  function _shareLink() {
    if (_submitting) return;
    if (_atheneumGL?.apiCallInProgress === true) {
      _pendingSubmit = _shareLink;
      return;
    }
    if (_formAPICallInProgress === true) {
      _pendingSubmit = _sendForm;
      return;
    }

    // Moved offline check to be an early exit
    if (
      SalesToolkit.CrossPlatform.enabled &&
      !SalesToolkit.CrossPlatform.isOnline()
    ) {
      const message = SalesToolkit.Translation.get(
        "unavailableOfflineMessage",
        "This feature is not available offline. Please check your connection and try again.",
      );
      const title = SalesToolkit.Translation.get(
        "unavailableOfflineTitle",
        "Offline",
      );
      SalesToolkit.UI.Notifications.showCustomNotification(
        "info",
        message,
        title,
      );
      return;
    }

    _submitting = true;
    _pendingSubmit = null;

    const shareButton = document.getElementById("salestoolkit-sharelink");
    _parent.trackAppClick(
      shareButton,
      "#salestoolkit-sharelink",
      "trackingFormShareLinkRequested",
      SalesToolkit.Translation.get(
        "trackingFormShareLinkRequested",
        "trackingFormShareLinkRequested",
      ),
    );

    const callbacks = {
      success: _handleShareLinkSuccess,
      failure: _handleShareLinkFailure,
    };

    _parent.submitForm(
      _form,
      document.getElementById("form-holder"),
      callbacks,
      true,
      _populatedImage ?? null,
    );
  }

  /**
   * Handles the success callback for a share link submission.
   */
  function _handleShareLinkSuccess() {
    _populatedImage = null;
    _submitting = false;
    let message = SalesToolkit.Translation.get("success", "Success!");
    let title = SalesToolkit.Translation.get("submitted", "Submitted");
    if (_form?.properties) {
      message =
        SalesToolkit.Translation.get(
          _form.properties.submittedMessage,
          _form.properties.submittedMessage,
        ) || message;
      title =
        SalesToolkit.Translation.get(
          _form.properties.submittedTitle,
          _form.properties.submittedTitle,
        ) || title;
    }

    if (
      SalesToolkit.Theme.getBooleanProperty("clearFormAfterSendLink", false)
    ) {
      _parent.clearForm(document.getElementById("form-holder"));
    }
    SalesToolkit.UI.Notifications.showCustomNotification(
      "info",
      message,
      title,
    );
  }

  /**
   * Handles the failure callback for a share link submission.
   */
  function _handleShareLinkFailure(
    reason,
    httpStatus,
    errorCode,
    formData,
    functionName,
    json,
  ) {
    _populatedImage = null;
    _submitting = false;
    let title = SalesToolkit.Translation.get("errorTitle", "Error!");
    if (_form?.properties) {
      title =
        SalesToolkit.Translation.get(
          _form.properties.submittedErrorTitle,
          _form.properties.submittedErrorTitle,
        ) || title;
    }

    let type = "info";
    let message = "";
    let errorCodeHex;
    if (typeof errorCode === "number") {
      errorCodeHex = errorCode.toString(16).padStart(8, "0");
    }

    if (json?.missingParams?.length > 0) {
      type = "system";
      message = SalesToolkit.Translation.getOrGetThemeKey(
        "error" + errorCodeHex,
        SalesToolkit.Translation.get(
          "notificationFormSubmitFailure",
          "Form could not be submitted",
        ),
      );
      if (
        SalesToolkit.Theme.getBooleanProperty(
          "includeErrorCodeForFormSubmission",
          true,
        ) &&
        message === "Form could not be submitted"
      ) {
        message += ` - ${errorCode}`;
      }
      SalesToolkit.UI.Notifications.showCustomNotification(
        type,
        message,
        title,
      );

      json.missingParams.forEach((name) => {
        const field = document.querySelector(`[name="${name}"]`);
        const label = document.querySelector(`label[for="${name}"]`);
        field?.classList.add("invalid", "previously-invalid");
        label?.classList.add("invalid", "previously-invalid");
      });
      return;
    }

    if (Atheneum.Callbacks.isOffline(reason, httpStatus, errorCode)) {
      title = SalesToolkit.Translation.get(
        "offlineSubmissionTitle",
        "Offline Submission",
      );
      message = SalesToolkit.Translation.get(
        "offlineSubmissionMessage",
        "Form submitted while offline, once a connection is established the form will be sent",
      );
      if (_form?.properties) {
        message =
          SalesToolkit.Translation.get(
            _form.properties.submittedMessage,
            _form.properties.submittedMessage,
          ) || message;
        title =
          SalesToolkit.Translation.get(
            _form.properties.submittedOfflineTitle,
            _form.properties.submittedOfflineTitle,
          ) || title;
      }
      _parent.clearForm(document.getElementById("form-holder"));

      if (_form.type === SalesToolkit.Forms.Types.CREATE_MEETING) {
        if (
          !formData.shareLink &&
          SalesToolkit.Theme.getBooleanProperty(
            "goToMeetingsScreenWhenOffline",
            false,
          )
        ) {
          _parent.showMeetingsScreen();
        }
      }
    } else if (errorCode === -1086) {
      let key = "notificationFormValidationFailure";
      let fields = _form.fields.filter(
        (field) =>
          !field.required &&
          field.properties?.hasOwnProperty("requiredForNonLink"),
      );
      for (const field of fields) {
        const $field = $('[name="' + field.name + '"]');
        if ($field.length > 0 && !$.trim($field.val())) {
          key =
            "notificationFormValidationFailureWithConditionalRequiredFields";
          break;
        }
      }
      message = SalesToolkit.Translation.get(
        key,
        "Form validation failed, please check each field and try again.",
      );
    } else {
      // This covers the remaining specific hex codes and the generic final 'else'
      const errorKey = `error${errorCodeHex}`;
      const defaultMessageKey =
        ["08071400", "08071706", "08071707", "08071708"].includes(
          errorCodeHex,
        ) ||
        ["08071712", "08071713", "08071714", "08071715"].includes(errorCodeHex)
          ? SalesToolkit.API.ErrorCodes.getName(errorCode)
          : SalesToolkit.Translation.get(
              "notificationFormSubmitFailure",
              "Form could not be submitted",
            );

      message = SalesToolkit.Translation.getOrGetThemeKey(
        errorKey,
        defaultMessageKey,
      );
      type = "system";

      if (
        SalesToolkit.Theme.getBooleanProperty(
          "includeErrorCodeForFormSubmission",
          true,
        ) &&
        message === "Form could not be submitted"
      ) {
        message += ` - ${errorCode}`;
      }
    }
    SalesToolkit.UI.Notifications.showCustomNotification(type, message, title);
  }
  //#endregion

  //#region Breadcrumbs
  /**
   * Adds a single breadcrumb link to the breadcrumb container.
   * @param {HTMLElement} breadcrumbsEl The container element for breadcrumbs.
   * @param {string} text The text for the breadcrumb link.
   * @param {string} collectionId The collection ID for the navigation action.
   */
  function _addBreadcrumb(breadcrumbsEl, text, collectionId) {
    const listItem = document.createElement("li");
    // Use data-module-action for navigation instead of href
    listItem.innerHTML = `<i class="fa fa-angle-right"></i> <a class="breadsmall" href="#" data-module-action="loadIndexFromBreadcrumb" data-collection-id="${collectionId}">${text}</a>`;
    breadcrumbsEl.appendChild(listItem);
  }

  /**
   * Clears existing breadcrumbs and adds the root (Home) link.
   * @param {HTMLElement} breadcrumbsEl The container element for breadcrumbs.
   * @param {string} text The text for the root link.
   */
  function _addRootBreadcrumb(breadcrumbsEl, text) {
    breadcrumbsEl.innerHTML = "";
    const translationKey =
      { Home: "homeButton", Library: "libraryButton" }[text] || "homeButton";
    const translatedText = SalesToolkit.Translation.get(translationKey, text);

    const listItem = document.createElement("li");
    // Use data-module-action for navigation instead of href
    listItem.innerHTML = `<i class="fa fa-home"></i> <a href="#" data-module-action="loadHome" data-translation-rendered="true" data-translation-key="${translationKey}" data-translation-default="${text}">${translatedText}</a>`;
    breadcrumbsEl.appendChild(listItem);
  }

  /**
   * Renders the full breadcrumb trail for the current form.
   * @param {string | null} collectionId The current collection ID from the URL.
   */
  function _renderBreadcrumbs(collectionId) {
    const breadcrumbsEl = document.getElementById("salestoolkit-breadcrumbs");
    if (!breadcrumbsEl) return;

    _addRootBreadcrumb(breadcrumbsEl, "Home");

    const path = [];
    let currentItem =
      SalesToolkit.Library.collections[_form.collectionId] ||
      SalesToolkit.Library.collections[collectionId];

    if (!currentItem && _form.items?.length === 1) {
      currentItem = SalesToolkit.Library.collections[_form.items[0]];
    }

    // Build the path from the current item up to the root
    while (currentItem) {
      path.push(currentItem);
      currentItem = SalesToolkit.Library.collections[currentItem.containedBy];
    }
    path.reverse(); // Reverse to have the root first

    // Render the breadcrumbs for the collection path
    path.forEach((collection) => {
      // Don't show the breadcrumb for the home folder itself
      if (collection.id === SalesToolkit.getHomeCollectionID()) {
        return;
      }

      // Additional logic from original file to prevent showing breadcrumbs outside the main tree
      if (SalesToolkit.getHomeCollectionID()) {
        let parentId = collection.containedBy;
        let shouldShow = false;
        while (parentId != null) {
          if (parentId === SalesToolkit.getHomeCollectionID()) {
            shouldShow = true;
            break;
          }
          const parent = SalesToolkit.Library.collections[parentId];
          parentId = parent?.containedBy;
        }
        if (
          !shouldShow &&
          collection.containedBy !== SalesToolkit.getHomeCollectionID()
        )
          return;
      }

      _addBreadcrumb(breadcrumbsEl, collection.name, collection.id);
    });

    // Add the final breadcrumb for the form itself (not a link)
    const finalCrumb = document.createElement("li");
    finalCrumb.innerHTML = `<i class="fa fa-angle-right"></i> <span>${SalesToolkit.Translation.get(
      _form.title,
      _form.title,
    )}</span>`;
    breadcrumbsEl.appendChild(finalCrumb);
  }
  //#endregion

  //#region TREEVIEW
  /**
   * Initializes the TreeView component, sets up event listeners for navigation,
   * and handles scrolling to the active form item.
   */
  function _initTreeView() {
    const treeViewEl = document.getElementById("atheneum-tree-view");
    if (!treeViewEl) return;

    _treeView = SalesToolkit.Atheneum.UI.TreeView(
      treeViewEl,
      SalesToolkit.getHomeCollectionID(),
    );
    _treeView.populateTree();

    // Handle item clicks for navigation
    _treeView.on("item-click", function (item) {
      if (item.type === "collection") {
        _parent.navigator.loadIndexScreen(false, false, item.id);
        return;
      } else if (item.type === "asset" && item.id !== _treeView.selectedAsset) {
        if (SalesToolkit.Library.assets[item.id].mimetype === "asset/url") {
          SalesToolkit.Pages.openInNewTab(
            SalesToolkit.Library.assets[item.id].url,
          );
          return false;
        }
        _parent.navigator.loadAssetScreen(item.id, "?from=home");
      } else if (item.type === "form" && item.id !== _form.id) {
        _parent.navigator.loadFormScreen(item.id, "?from=home");
      }
      return false;
    });

    // Handle the 'loaded' event to scroll to the current form
    _treeView.element.addEventListener("loaded.jstree", function () {
      if (SalesToolkit.Theme.getBooleanProperty("scrollTreeView", false)) {
        setTimeout(function () {
          const treeViewContainer =
            document.getElementById("atheneum-tree-view");
          const formLiElement = document.getElementById(
            `${_treeView.puid}_form_${_form.id}`,
          );
          if (!treeViewContainer || !formLiElement) return;

          treeViewContainer.scrollTop = 0;

          // A simpler, more reliable way to calculate the scroll position
          const containerRect = treeViewContainer.getBoundingClientRect();
          const liRect = formLiElement.getBoundingClientRect();

          // Calculate the li's top position relative to the container's top
          let offsetTop = liRect.top - containerRect.top;

          offsetTop -= 7; // Original adjustment
          if (offsetTop < 0) {
            offsetTop = 0;
          }

          treeViewContainer.scrollTop = offsetTop;
        }, 350);
      }
    });
  }

  /**
   * Toggles the visibility of the tree view panel and adjusts the main panel's width accordingly.
   */
  function _toggleTree() {
    const mainPanel = document.getElementById("main-panel");
    const treePanel = document.getElementById("tree-view-panel");
    if (!mainPanel || !treePanel) return;

    let mainLG = 12;
    let mainMD = 12;
    let mainSM = 12;

    if (treePanel.style.display === "none") {
      // Show the tree
      const toggler = document.getElementById("tree-toggler");
      _parent.trackAppClick(
        toggler,
        "#tree-toggler",
        "trackingTreeViewToggleShow",
        SalesToolkit.Translation.get(
          "trackingTreeViewToggleShow",
          "trackingTreeViewToggleShow",
        ),
      );

      localStorage.removeItem("hideTree");
      treePanel.style.display = "block";
      mainLG -= _treeViewLargeCols;
      mainMD -= _treeViewMediumCols;
      mainSM -= _treeViewSmallCols;
    } else {
      // Hide the tree
      const toggler = document.getElementById("tree-toggler");
      _parent.trackAppClick(
        toggler,
        "#tree-toggler",
        "trackingTreeViewToggleHide",
        SalesToolkit.Translation.get(
          "trackingTreeViewToggleHide",
          "trackingTreeViewToggleHide",
        ),
      );

      localStorage.setItem("hideTree", "true");
      treePanel.style.display = "none";
    }

    _removeAllColCSS(mainPanel);
    mainPanel.classList.add(`col-lg-${mainLG}`);
    mainPanel.classList.add(`col-md-${mainMD}`);
    mainPanel.classList.add(`col-sm-${mainSM}`);
  }
  //#endregion

  //#region Bussiness Card Scanning
  /**
   * Opens the camera modal, starts the video stream, and sets up the UI for scanning.
   */
  async function _openedCameraModal() {
    const cameraButton = document.getElementById("salestoolkit-camera");
    _parent.trackAppClick(
      cameraButton,
      "#salestoolkit-camera",
      "trackingCameraModalForm",
      SalesToolkit.Translation.get(
        "trackingCameraModalForm",
        "trackingCameraModalForm",
      ),
    );

    const scanInfoEl = document.getElementById("scan-info");
    if (scanInfoEl) {
      scanInfoEl.style.display = "none";
      scanInfoEl.textContent = "";
    }

    const result = await _cardReader.startStream(
      SalesToolkit.Theme.getBooleanProperty("scannerPreferBackCamera", true),
    );

    if (!result) {
      const notificationBody = SalesToolkit.Translation.get(
        "notificationNoDeviceFound",
        "No camera found",
      );
      SalesToolkit.UI.Notifications.showCustomNotification(
        "info",
        notificationBody,
      );
    } else {
      _parent.showModal("cameraModal");

      setTimeout(() => {
        _updateScanOverlay();
      }, 500);

      if (
        SalesToolkit.Theme.getBooleanProperty("scanAutomaticallyForm", false)
      ) {
        if (autoScanTimeout) clearTimeout(autoScanTimeout);
        autoScanTimeout = setTimeout(_takeScan, 500, true);
      }
    }
  }

  /**
   * Stops the camera stream and resets the scan info UI.
   */
  function _stopCamera() {
    if (autoScanTimeout) {
      clearTimeout(autoScanTimeout);
      autoScanTimeout = null;
    }

    if (_cardReader) {
      _cardReader.stopStream();
    }

    const scanInfoEl = document.getElementById("scan-info");
    if (scanInfoEl) {
      scanInfoEl.style.display = "none";
      scanInfoEl.textContent = "";
    }
  }

  /**
   * Handles the successful lookup of a badge after a barcode scan.
   * @param {object} badge - The badge data returned from the API.
   * @param {string} decodedText - The raw text decoded from the barcode.
   */
  function _handleBadgeLookupSuccess(badge, decodedText) {
    _endProcessing();
    _parent.hideModal("cameraModal");
    _stopCamera();

    const formHolder = document.getElementById("form-holder");
    _parent.populateFormWithBadgeData(formHolder, badge);

    const badgeInput = formHolder.querySelector('input[data-type="BADGE"]');
    if (badgeInput) {
      badgeInput.value = decodedText;
    }

    const notificationBody = SalesToolkit.Translation.get(
      "notificationCardReaderFormFilled",
      "Form information updated",
    );
    SalesToolkit.UI.Notifications.showCustomNotification(
      "info",
      notificationBody,
    );
  }

  /**
   * Handles the failure of a badge lookup after a barcode scan.
   * @param {string} reason - The reason for failure.
   * @param {number} httpStatus - The HTTP status code.
   * @param {number} errorCode - The specific error code.
   * @param {boolean} autoScanning - Whether the scan was initiated automatically.
   */
  function _handleBadgeLookupFailure(
    reason,
    httpStatus,
    errorCode,
    autoScanning,
  ) {
    _endProcessing();
    Atheneum.Callbacks.logFailureDetails(
      reason,
      httpStatus,
      errorCode,
      "Atheneum.Badges.lookup",
    );

    let message;
    if (Atheneum.Callbacks.isOffline(reason, httpStatus, errorCode)) {
      message = SalesToolkit.Theme.getStringProperty(
        "badgeScanOffline",
        "No internet connection available, please try again once a connection has been established or complete the form manually. Thank you",
      );
    } else {
      const errorCodeHex =
        typeof errorCode === "number"
          ? errorCode.toString(16).padStart(8, "0")
          : "";
      message = SalesToolkit.Translation.getOrGetThemeKey(
        "error" + errorCodeHex,
        SalesToolkit.Translation.get(
          "badgeScanError",
          "Ran into an error while scanning badge",
        ),
      );
      if (
        SalesToolkit.Theme.getBooleanProperty(
          "includeErrorCodeForBadgeScan",
          true,
        ) &&
        message ===
          SalesToolkit.Translation.get(
            "badgeScanError",
            "Ran into an error while scanning badge",
          )
      ) {
        message += ` - ${errorCode}`;
      }
    }

    const scanInfoEl = document.getElementById("scan-info");
    if (scanInfoEl) {
      scanInfoEl.textContent = message;
      scanInfoEl.style.display = "block";
    }

    if (autoScanning) {
      setTimeout(
        _takeScan,
        SalesToolkit.Theme.getIntegerProperty("timeBetweenAutoscans", 100),
        true,
      );
    }
  }

  /**
   * Attempts to scan for and process a barcode or QR code from the camera feed.
   * @param {boolean} autoScan - Whether the scan was initiated automatically.
   * @returns {Promise<boolean>} A promise that resolves to true if a barcode was found and processed.
   */
  async function _attemptBarcodeScan(autoScan) {
    if (!SalesToolkit.Theme.getBooleanProperty("scanForBarcodesForm", false)) {
      return false;
    }

    try {
      const imageCrop = _cardReader.getCropAreaImage(
        SalesToolkit.Theme.getIntegerProperty("scanAreaWidth", 300),
        SalesToolkit.Theme.getIntegerProperty("scanAreaHeight", 300),
      );
      const blob = await fetch(imageCrop).then((it) => it.blob());
      const imageFile = new File([blob], "scan", { type: "image/jpeg" });
      const decodedText = await _html5QrCode.scanFile(imageFile, true);

      if (decodedText) {
        SalesToolkit.Badges.lookup(decodedText, {
          success: (badge) => _handleBadgeLookupSuccess(badge, decodedText),
          failure: (r, h, e) => _handleBadgeLookupFailure(r, h, e, autoScan),
          offline: (r, h, e) => _handleBadgeLookupFailure(r, h, e, autoScan), // Treat offline as a failure for this handler
        });
        return true; // Barcode found, processing is handed off.
      }
    } catch (err) {
      console.log("Error trying to decode QR / bar code:", err);
    }
    return false; // No barcode found or an error occurred.
  }

  /**
   * Attempts to scan for and process a business card from a captured image.
   * @param {string} imageUrl - The base64 data URL of the captured image.
   * @param {boolean} autoScan - Whether the scan was initiated automatically.
   */
  async function _attemptBusinessCardScan(imageUrl, autoScan) {
    // Guard clauses for business card scanning
    if (
      !SalesToolkit.Theme.getBooleanProperty("scanForBusinessCardsForm", true)
    ) {
      if (autoScan) {
        // If only scanning barcodes, show "not detected" and retry
        const scanInfoEl = document.getElementById("scan-info");
        if (scanInfoEl) {
          scanInfoEl.textContent = SalesToolkit.Translation.get(
            "notificationCardReaderNoBadge",
            "Badge not detected",
          );
          scanInfoEl.style.display = "block";
        }
        setTimeout(
          _takeScan,
          SalesToolkit.Theme.getIntegerProperty("timeBetweenAutoscans", 100),
          true,
        );
      }
      _endProcessing();
      return;
    }
    if (
      SalesToolkit.Theme.getBooleanProperty("busScanningManualOnly", true) &&
      autoScan
    ) {
      const scanInfoEl = document.getElementById("scan-info");
      if (scanInfoEl) {
        scanInfoEl.textContent = SalesToolkit.Translation.get(
          "notificationCardReaderNoBadgeNoAutoBus",
          "Badge not detected. If scanning a business card please position and use the Take Picture button",
        );
        scanInfoEl.style.display = "block";
      }
      setTimeout(
        _takeScan,
        SalesToolkit.Theme.getIntegerProperty("timeBetweenAutoscans", 100),
        true,
      );
      _endProcessing();
      return;
    }

    try {
      const result = await _cardReader.getCardDetails(imageUrl);

      const isResultEmpty =
        (!result.emails || result.emails.length === 0) &&
        (!result.phones || result.phones.length === 0) &&
        (!result.jobs || result.jobs.length === 0) &&
        (!result.websites || result.websites.length === 0) &&
        (!result.addresses || result.addresses.length === 0) && 
        (!result.firstName || result.firstName.trim() === "") &&
        (!result.lastName || result.lastName.trim() === "");


      if (isResultEmpty) {
        throw new Error("Empty business card result");
      }

      if (
        SalesToolkit.Theme.getBooleanProperty(
          "rejectCardsWithoutEmailForm",
          false,
        ) &&
        !result.emails?.length
      ) {
        const message = SalesToolkit.Translation.get(
          "notificationCardReaderErrorNoEmail",
          "Could not get an email address from the business card",
        );
        if (!autoScan) {
          SalesToolkit.UI.Notifications.showCustomNotification("info", message);
        } else {
          const scanInfoEl = document.getElementById("scan-info");
          if (scanInfoEl) {
            scanInfoEl.textContent = message;
            scanInfoEl.style.display = "block";
          }
          setTimeout(
            _takeScan,
            SalesToolkit.Theme.getIntegerProperty("timeBetweenAutoscans", 100),
            true,
          );
        }
        _endProcessing();
        return;
      }

      _populatedFromCard = false;
      _autoScan = autoScan;
      _populatedImage = imageUrl;

      const correctedResult = _parent.checkForTitleBug(result);

      if (
        SalesToolkit.Theme.getBooleanProperty(
          "useCardDetailsDirectlyForm",
          false,
        )
      ) {
        _parent.hideModal("cameraModal");
        _parent.populateFormFromScan(
          document.getElementById("form-holder"),
          correctedResult,
        );

        if (
          !SalesToolkit.Theme.getBooleanProperty("scanForContactsTooForm", true)
        ) {
          SalesToolkit.UI.Notifications.showCustomNotification(
            "info",
            SalesToolkit.Translation.get(
              "notificationCardReaderFormFilled",
              "Form information updated",
            ),
          );
          _stopCamera();
          _endProcessing();
          return;
        } else {
          _stopCamera();
          _populatedFromCard = true;
        }
      }

      await _atheneumGL.performLookupFromScanResults(correctedResult);
      _endProcessing();
    } catch (err) {
      _endProcessing();
      const scanInfoEl = document.getElementById("scan-info");
      let message;
      if (err instanceof TypeError && err.message === "Failed to fetch") {
        message = SalesToolkit.Translation.get(
          "notificationCardReaderErrorOffline",
          "You are offline. Please check your connection and try again.",
        );
        SalesToolkit.UI.Notifications.showCustomNotification("info", message);
      } else {
        message = SalesToolkit.Translation.get(
          "notificationCardReaderError",
          "No badge or business card detected",
        );
        if (!autoScan) {
          SalesToolkit.UI.Notifications.showCustomNotification("info", message);
        } else {
          if (scanInfoEl) {
            scanInfoEl.textContent = message;
            scanInfoEl.style.display = "block";
          }
          setTimeout(
            _takeScan,
            SalesToolkit.Theme.getIntegerProperty("timeBetweenAutoscans", 100),
            true,
          );
        }
      }
    }
  }

  /**
   * Captures an image from the camera feed and attempts to process it as a barcode or business card.
   * @param {boolean} [autoScan=false] - Whether the scan was initiated automatically.
   */
  async function _takeScan(autoScan = false) {
    _setupProcessing();
    if (!autoScan) {
      _parent.trackAppClick(
        document.getElementById("cameraModal"),
        "#salestoolkit-camera",
        "trackingCameraModalTakeScan",
        SalesToolkit.Translation.get(
          "trackingCameraModalTakeScan",
          "trackingCameraModalTakeScan",
        ),
      );
    }

    if (_cardReader.stream == null) {
      _endProcessing();
      return;
    }

    const imageUrl = _cardReader.getImage();
    _cardReader.form = _form; // Assign form context to card reader

    if (SalesToolkit.Theme.getBooleanProperty("clearFormBeforeScan", true)) {
      _parent.clearForm(document.getElementById("form-holder"));
    }

    // First, try to find a barcode. If successful, it will return true and stop.
    if (await _attemptBarcodeScan(autoScan)) {
      return;
    }

    // If no barcode was found, proceed to attempt a business card scan.
    await _attemptBusinessCardScan(imageUrl, autoScan);
  }
  //#endregion

  //#region Remote Completion
  /**
   * Resets the inactivity timer. Called on start and on user activity.
   */
  function _resetTimer() {
    clearTimeout(_mainTimeoutId);
    clearInterval(_countdownIntervalId);
    clearTimeout(_countdownStarterId);
    console.log("Inactivity timer reset due to user activity.");

    const span = document.getElementById("externalQRCode");
    if (span) {
      span.textContent = SalesToolkit.Translation.get(
        "remoteCompletionWaiting",
        "Waiting for remote completion...",
      );
    }

    const timeoutInMS = SalesToolkit.Theme.getIntegerProperty(
      "remoteCompletionTimeout",
      60000,
    );
    const warningTimeInMS = SalesToolkit.Theme.getIntegerProperty(
      "remoteCompletionCountdownTime",
      1000 * 10,
    );

    // Set the main timeout that calls _checkRemoteCompletionEnd with 'false' for timeout
    _mainTimeoutId = setTimeout(
      () => _checkRemoteCompletionEnd(false),
      timeoutInMS,
    );

    // Set a timeout to start the warning countdown
    const countdownStartTime = timeoutInMS - warningTimeInMS;
    _countdownStarterId = setTimeout(
      () => _startCountdown(warningTimeInMS),
      countdownStartTime,
    );
  }

  /**
   * The single handler for any user activity during remote completion.
   */
  function _handleUserActivity() {
    _resetTimer();
  }

  /**
   * Ends the remote completion process, cleans up timers and listeners, and navigates back.
   * @param {boolean} [completed=true] - Whether the completion was successful or a timeout.
   */
  function _checkRemoteCompletionEnd(completed = true) {
    if (_remoteCompletionWaiting) {
      _remoteCompletionWaiting = false;

      // Clear all timers and intervals
      clearTimeout(_mainTimeoutId);
      clearInterval(_countdownIntervalId);
      clearInterval(_pollingIntervalId);
      clearTimeout(_countdownStarterId);

      // Remove activity listeners
      _activityEvents.forEach((eventType) => {
        window.removeEventListener(eventType, _handleUserActivity);
      });

      _parent.hideModal("qrCodeModal");
      _submitting = false;

      const notificationBody = completed
        ? SalesToolkit.Translation.get(
            "remoteCompletionFinishedSuccess",
            "Remote completion was successful",
          )
        : SalesToolkit.Translation.get(
            "remoteCompletionTimeoutMessage",
            "Remote completion timed out - if the qr code was scanned you can still complete it on your phone.",
          );

      localStorage.setItem(
        "notificationsToShow",
        JSON.stringify([{ body: notificationBody, title: "", type: "info" }]),
      );
      _backButtonHome();
    }
  }

  /**
   * Starts the visual countdown on the screen.
   * @param {number} warningTimeInMS - The duration of the countdown in milliseconds.
   */
  function _startCountdown(warningTimeInMS) {
    if (!_remoteCompletionWaiting) return;

    let secondsLeft = warningTimeInMS / 1000;
    const span = document.getElementById("externalQRCode");
    if (!span) return;

    span.innerHTML = SalesToolkit.Translation.getFormatted(
      "remoteCompletionCountdown",
      "This screen will close in {0} seconds",
      [secondsLeft],
    );

    _countdownIntervalId = setInterval(() => {
      secondsLeft--;
      if (secondsLeft > 0) {
        span.innerHTML = SalesToolkit.Translation.getFormatted(
          "remoteCompletionCountdown",
          "This screen will close in {0} seconds",
          [secondsLeft],
        );
      } else {
        clearInterval(_countdownIntervalId);
      }
    }, 1000);
  }

  /**
   * Starts polling for the status of a remote completion session.
   * @param {string} requestId The ID of the remote completion session.
   */
  function _startRemoteCompletion(requestId) {
    const span = document.getElementById("externalQRCode");
    _remoteCompletionWaiting = true;

    _activityEvents.forEach((eventType) => {
      window.addEventListener(eventType, _handleUserActivity);
    });

    if (span) {
      span.textContent = SalesToolkit.Translation.get(
        "remoteCompletionWaiting",
        "Waiting for remote completion...",
      );
    }

    _pollingIntervalId = setInterval(() => {
      SalesToolkit.API.pollRemoteCompletionStatus(requestId, {
        success: function (json) {
          const count = json.count;
          const countToWaitFor = SalesToolkit.Theme.getIntegerProperty(
            "remoteCompletionCountToWaitFor",
            1,
          );
          if (
            (json.singleUse === true && json.complete === true) ||
            (json.singleUse === false && count >= countToWaitFor)
          ) {
            _checkRemoteCompletionEnd(true); // End successfully
          }
        },
        failure: function (reason, httpstatus, errorCode) {
          console.error(
            "Could not poll for remote completion:",
            reason,
            httpstatus,
            errorCode,
          );
        },
      });
    }, 5000); // Poll every 5 seconds

    _resetTimer();
  }
  //#endregion

  //#region Form History
  /**
   * Checks for a formHistoryId in the URL and loads the data if found.
   */
  async function _loadFromFormHistory(shareWaited = 0) {
    const formHistoryId =
      SalesToolkit.Pages.CurrentPage.getQueryParam("formHistoryId");
    const shares = SalesToolkit.Summary.shares;
    if (shareWaited <= 3 && (!shares || shares.length === 0)) {
      setTimeout(() => _loadFromFormHistory(shareWaited + 1), 500);
      return;
    }
    if (formHistoryId && shares?.some((x) => x.id === formHistoryId)) {
      if (!SalesToolkit.Contacts.contactsLoaded) {
        setTimeout(_loadFromFormHistory, 500);
      } else {
        const formHistory = shares.find((x) => x.id === formHistoryId);
        if (formHistory) {
          _parent.setFormHistoryId(formHistoryId);
          const data = formHistory.fields;
          const formHolder = document.getElementById("form-holder");

          if (data?.CONTACT_ID) {
            const contact = await SalesToolkit.Contacts.getById(
              data.CONTACT_ID,
            );
            if (
              contact &&
              SalesToolkit.Theme.getBooleanProperty(
                "loadContactsFromHistory",
                true,
              )
            ) {
              _parent.populateFormWithContact(formHolder, contact, true);
              return;
            }
          }
          _parent.populateFromObj(data, formHolder);

          if (
            SalesToolkit.Theme.getBooleanProperty(
              "showLoadedFromHistoryNotif",
              true,
            )
          ) {
            const message = SalesToolkit.Translation.get(
              "loadFromHistoryTitle",
              "Loaded form from history",
            );
            const title = SalesToolkit.Translation.get(
              "loadFromHistoryMessage",
              "Please be aware: some entries may no longer be available.",
            );
            SalesToolkit.UI.Notifications.showCustomNotification(
              "info",
              message,
              title,
            );
          }
        }
      }
    }
  }

  /**
   * Loads the most recent form history entry.
   */
  async function _loadLastFormEntry() {
    if (!SalesToolkit.Contacts.contactsLoaded) {
      setTimeout(async () => await _loadLastFormEntry(), 1000);
    } else {
      const formHistory = SalesToolkit.Summary.shares.find(
        (x) => x.type === "forms",
      );
      if (!formHistory) return;

      _parent.setFormHistoryId(formHistory.id);
      const data = formHistory.fields;
      const formHolder = document.getElementById("form-holder");

      if (data?.CONTACT_ID) {
        const contact = await SalesToolkit.Contacts.getById(data.CONTACT_ID);
        if (
          contact &&
          SalesToolkit.Theme.getBooleanProperty("loadContactsFromHistory", true)
        ) {
          _parent.populateFormWithContact(formHolder, contact, true);
          return;
        }
      }
      _parent.populateFromObj(data, formHolder);
    }
  }

  /**
   * Sets up event listeners on the form for a long-press action to load the last entry.
   */
  function _setupLongTouchListener() {
    if (
      !SalesToolkit.Theme.getBooleanProperty(
        "allowLongTouchLoadLastHistory",
        false,
      )
    ) {
      return;
    }

    let formLTTimer = null;
    const touchDuration = SalesToolkit.Theme.getIntegerProperty(
      "longTouchHistoryLoadTimerInMS",
      1500,
    );
    const formHolder = document.getElementById("form-holder");
    if (!formHolder) return;

    const onFormLongTouch = () => {
      clearTimeout(formLTTimer);
      formLTTimer = null;
      _loadLastFormEntry();
    };

    const formTouchStart = () => {
      if (!formLTTimer) {
        formLTTimer = setTimeout(onFormLongTouch, touchDuration);
      }
    };

    const formTouchEnd = () => {
      if (formLTTimer) {
        clearTimeout(formLTTimer);
        formLTTimer = null;
      }
    };

    formHolder.addEventListener("mousedown", formTouchStart, false);
    formHolder.addEventListener("mouseup", formTouchEnd, false);
    // Add touch events for mobile devices
    formHolder.addEventListener("touchstart", formTouchStart, false);
    formHolder.addEventListener("touchend", formTouchEnd, false);
  }

  function _getReadableDate(data) {
    const tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
    const date = new Date(data - tzoffset);
    return (
      date.toISOString().split("T")[0] +
      " " +
      date.toISOString().split("T")[1].substring(0, 5)
    );
  }

  function _buildFormHistoryDropdown() {
    const dropdownEl = document.getElementById("formHistoryDropdown");
    if (!dropdownEl) return;

    dropdownEl.innerHTML = ""; // .empty()

    let formHistories = SalesToolkit.Summary.shares.filter(
      (x) => x.type === "forms" && x.userId === SalesToolkit.User.id,
    );
    let entries = formHistories.map((v) => ({
      ...v,
      label: SalesToolkit.Theme.getStringProperty(
        "formHistoryDropdownLabelTemplate",
        '<b>{submissionDate}</b><br /><span class="email" >{email}</span>',
      )
        .replace(
          "{email}",
          v.fields.email ||
            v.fields.to ||
            SalesToolkit.Translation.get("unknown", "Unknown"),
        )
        .replace("{submissionDate}", _getReadableDate(v.submissionDate)),
      name: v.id,
    }));

    let data = {
      label: SalesToolkit.Translation.get(
        "formHistoriesDropdownName",
        "History",
      ),
      name: "History", // The name is used internally by the dropdown
      options: entries,
    };

    let options = {
      multiSelect: false,
      showSelectionCount: false,
      showZeroSelectionCount: false,
      showResults: false,
      maxItems: SalesToolkit.Theme.getIntegerProperty(
        "historyFiltersMaxItemsShown",
        15,
      ),
      maxHeight: SalesToolkit.Theme.getIntegerProperty(
        "historyFiltersMaxHeight",
        644,
      ),
      allowSearchTerms: false,
      backgroundImagePadding: SalesToolkit.Theme.getIntegerProperty(
        "historyFilterImagePadding",
        24,
      ),
      itemPaddingTopBottom: SalesToolkit.Theme.getIntegerProperty(
        "historyFilterItemPaddingTopBottom",
        12,
      ),
      showClearAllButton: false,
      hideEntriesWithZeroResults: false,
      quickDeleteHeight: 0,
      quickDeletePadding: 0,
      buttonTopBottomPadding: 7,
      localOnly: true,
    };

    let filteredDropdown = new FilteredDropdown(
      dropdownEl,
      undefined,
      data,
      options,
    );
    filteredDropdown.onchange = _selectedFormHistoryChanged;
    dropdownEl.style.display = "inline-block";

    // Note: The offset calculation below relies on jQuery for accuracy and simplicity.
    const top = _getOffset(dropdownEl).top;
    const footer = document.querySelector(".page-footer");
    const footerTop = footer ? _getOffset(footer).top : 0;

    const realMaxHeight = footerTop - top - 75;
    if (realMaxHeight < options.maxHeight) {
      const itemsEl = document.querySelector(".filteredDropdown-items");
      if (itemsEl) {
        itemsEl.style.maxHeight = `${realMaxHeight}px`;
      }
    }
  }

  async function _selectedFormHistoryChanged(
    entryName,
    active,
    element,
    filteredDropdown,
  ) {
    const currentRequestNumber = ++_formLoadRequestCounter;
    const formHolder = document.getElementById("form-holder");

    if (filteredDropdown.selectedItems.length === 0) {
      _parent.clearForm(formHolder);
      return;
    }

    if (!SalesToolkit.Contacts.contactsLoaded) {
      setTimeout(
        () =>
          _selectedFormHistoryChanged(
            entryName,
            active,
            element,
            filteredDropdown,
          ),
        1000,
      );
      return;
    }

    filteredDropdown.selectedItems = [];
    const formHistory = SalesToolkit.Summary.shares.find(
      (x) => x.type === "forms" && x.id === entryName,
    );

    filteredDropdown.hideDropdown();

    if (formHistory) {
      _parent.setFormHistoryId(formHistory.id);

      if (
        SalesToolkit.Theme.getBooleanProperty(
          "showLoadedFromHistoryNotif",
          true,
        )
      ) {
        const message = SalesToolkit.Translation.get(
          "loadFromHistoryTitle",
          "Loaded form from history",
        );
        const title = SalesToolkit.Translation.get(
          "loadFromHistoryMessage",
          "Please be aware: some entries may no longer be available.",
        );
        SalesToolkit.UI.Notifications.showCustomNotification(
          "info",
          message,
          title,
        );
      }

      const data = formHistory.fields;

      if (data?.CONTACT_ID) {
        let contact = await SalesToolkit.Contacts.getById(data.CONTACT_ID);
        if (currentRequestNumber !== _formLoadRequestCounter) {
          console.log("Stale form history request ignored.");
          return;
        }

        if (Array.isArray(contact)) {
          if (contact.length === 0) {
            contact = null;
          } else {
            contact = contact[0];
          }
        }
        if (
          contact &&
          SalesToolkit.Theme.getBooleanProperty("loadContactsFromHistory", true)
        ) {
          _parent.populateFormWithContact(formHolder, contact, true);
          return;
        }
      }
      _parent.populateFromObj(data, formHolder);
    }
  }
  //#endregion

  //#region Global Lookup
  async function _globalLookupCallback(message) {
    console.log(JSON.stringify(message));
    const scanInfoEl = document.getElementById("scan-info");

    if (message.status === "populateContact") {
      _executePendingSubmit();
    } else if (message.status === "suggestions") {
      const field = document
        .getElementById("form-holder")
        ?.querySelector("input[data-type='EMAIL']");
      if (field) {
        await _parent.showContactSuggestions(field, message.contacts);
      }
    } else if (
      message.status === "setupForNoContacts" &&
      message.hasOwnProperty("fromScan")
    ) {
      if (_populatedFromCard) {
        SalesToolkit.UI.Notifications.showCustomNotification(
          "info",
          SalesToolkit.Translation.get(
            "notificationCardReaderShareFilled",
            "Share form information updated",
          ),
        );
        _stopCamera();
        _parent.hideModal("cameraModal");
      } else if (!_autoScan) {
        SalesToolkit.UI.Notifications.showCustomNotification(
          "info",
          SalesToolkit.Translation.get(
            "notificationCardReaderNoMatch",
            "No existing contact could be found to match the scan",
          ),
        );
        if (
          SalesToolkit.Theme.getBooleanProperty(
            "hideCameraOnFailedScanWhenNotAutoFilling",
            true,
          )
        ) {
          _stopCamera();
          _parent.hideModal("cameraModal");
        }
      } else if (
        SalesToolkit.Theme.getBooleanProperty(
          "keepScanningOnContactFailureForm",
          false,
        )
      ) {
        if (scanInfoEl) {
          scanInfoEl.textContent = SalesToolkit.Translation.get(
            "notificationCardReaderNoMatch",
            "No existing contact could be found to match the scan",
          );
          scanInfoEl.style.display = "block";
        }
        setTimeout(
          _takeScan,
          SalesToolkit.Theme.getIntegerProperty("timeBetweenAutoscans", 100),
          true,
        );
      } else {
        SalesToolkit.UI.Notifications.showCustomNotification(
          "info",
          SalesToolkit.Translation.get(
            "notificationCardReaderNoMatch",
            "No existing contact could be found to match the scan",
          ),
        );
        _stopCamera();
        _parent.hideModal("cameraModal");
      }
    } else if (message.status === "cameraStop") {
      _stopCamera();
      _parent.hideModal("cameraModal");
    } else if (message.status === "setupForContacts") {
      _parent.showModal("contactFilterModal");
    } else if (
      message.status === "populateForm" &&
      message.hasOwnProperty("fromScan")
    ) {
      _stopCamera();
      _parent.hideModal("cameraModal");
      let notificationBody = _populatedFromCard
        ? SalesToolkit.Translation.get(
            "notificationCardReaderShareFilledBoth",
            "Share form information updated from scan and matching contact",
          )
        : SalesToolkit.Translation.get(
            "notificationCardReaderMatch",
            "Contact loaded from scan",
          );
      SalesToolkit.UI.Notifications.showCustomNotification(
        "info",
        notificationBody,
      );
    }
  }

  function _setupGLU() {
    if (!SalesToolkit.Contacts.contactsLoaded) {
      setTimeout(_setupGLU, 1000);
      return;
    }

    // Instantiate the AtheneumGlobalLookup class and assign it to our private variable
    _atheneumGL = new AtheneumGlobalLookup(
      _form,
      document.getElementById("form-holder"),
      document.getElementById("contactFilterModal"),
      _globalLookupCallback, // Pass our private callback function
    );
  }
  /**
   * Handles the blur event on form inputs to trigger a contact lookup.
   * @param {FocusEvent} event The blur event.
   */
  async function _handleFormInputBlur(event) {
    // Ensure the event is on an input element
    if (event.target.tagName !== "INPUT") {
      return;
    }

    // Don't trigger lookup if focus is moving to a suggestion
    try {
      if (
        event.relatedTarget &&
        event.relatedTarget.classList.contains("suggestion")
      ) {
        return;
      }
    } catch (err) {
      console.log("Error checking relatedTarget:", err);
    }

    if (_atheneumGL) {
      await _atheneumGL.shouldPerformLookupForField(event.target);
    }
  }

  function _filtersStartOver() {
    if (_atheneumGL) {
      _atheneumGL.filtersStartOver();
    }
  }
  //#endregion

  //#region Handlers

  /**
   * Executes a pending submission function if one has been set.
   */
  function _executePendingSubmit() {
    if (
      SalesToolkit.Theme.getBooleanProperty("allowDelayedSubmits", false) &&
      typeof _pendingSubmit === "function"
    ) {
      const pendingFn = _pendingSubmit; // Copy the function reference
      _pendingSubmit = null; // Clear the pending function before executing
      pendingFn(); // Execute the original pending function
    }
  }

  /**
   * Event handler for when a signature pad is initialized by renderForm.
   * @param {CustomEvent} event The event containing the sigPad instance.
   */
  function _handleSigPadInit(event) {
    _sigPad = event.detail.sigPad;
  }

  /**
   * Updates the internal API call status flag based on a dispatched event.
   * @param {CustomEvent} event The event containing the API status.
   */
  function _handleApiCallStatusChange(event) {
    _formAPICallInProgress = event.detail.inProgress;
  }

  /**
   * Central click handler for all actions within the preview form module.
   * @param {MouseEvent} event The mouse event.
   */
  function _handleActionClick(event) {
    const target = event.target;
    const actionButton = target.closest("[data-action]");

    if (!actionButton) return;

    const action = actionButton.dataset.action;

    switch (action) {
      case "showTncsModal":
        _showTncsModal();
        break;
      case "openedCameraModal":
        _openedCameraModal();
        break;
      case "askToClearForm":
        _askToClearForm();
        break;
      case "sendForm":
        _sendForm();
        break;
      case "qrSubmit":
        _qrSubmit();
        break;
      case "shareLink":
        _shareLink();
        break;
      case "backButtonHome":
        _backButtonHome();
        break;
      case "fullscreen":
        document
          .querySelectorAll("body > .tooltip")
          .forEach((tip) => tip.remove());

        // Fix for Android resize issue on fullscreen
        if (
          SalesToolkit.CrossPlatform.enabled &&
          SalesToolkit.CrossPlatform.isAndroid()
        ) {
          window.dispatchEvent(new Event("resize"));
        }
        break;
      case "filtersStartOver":
        _filtersStartOver();
        break;
      case "stopCamera":
        _stopCamera();
        break;
      case "takeScan":
        _takeScan();
        break;
      case "clearForm":
        _parent.clearForm(document.getElementById("form-holder"));
        break;
    }
  }
  //#endregion

  //#region Private helpers
  /**
   * Calculates an element's offset (position) relative to the document.
   * @param {HTMLElement} element The element to measure.
   * @returns {{top: number, left: number}}
   */
  function _getOffset(element) {
    if (!element) {
      return { top: 0, left: 0 };
    }
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
    };
  }

  /**
   * Updates the position and size of the scan overlay to match the camera feed.
   */
  function _updateScanOverlay() {
    if (!SalesToolkit.Theme.getBooleanProperty("useScanOverlay", false)) return;

    const scanAreaDark = document.getElementById("scanAreaDark");
    const scanAreaLight = document.getElementById("scanAreaLight");
    const cameraFeed = document.getElementById("cameraFeedVideo");

    if (scanAreaDark && scanAreaLight && cameraFeed) {
      scanAreaDark.style.width = `${cameraFeed.offsetWidth}px`;
      scanAreaDark.style.height = `${cameraFeed.offsetHeight}px`;

      const rect = cameraFeed.getBoundingClientRect();
      scanAreaDark.style.top = `${rect.top + window.scrollY}px`;
      scanAreaDark.style.left = `${rect.left + window.scrollX}px`;

      scanAreaLight.style.width = `${SalesToolkit.Theme.getIntegerProperty(
        "scanAreaWidth",
        300,
      )}px`;
      scanAreaLight.style.height = `${SalesToolkit.Theme.getIntegerProperty(
        "scanAreaHeight",
        300,
      )}px`;

      scanAreaDark.style.display = "flex";
    }
  }

  /**
   * Parses the current window's URL query string into an object.
   * @returns {object} A key-value map of the URL parameters.
   */
  function _getUrlVars() {
    const vars = {};
    window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, (m, key, value) => {
      vars[key] = value;
      return value; // Return value is not used but is good practice
    });
    return vars;
  }

  function _backButtonHome() {
    if (_meetingId) {
      if (
        _parent.meetingFormHasChanges(
          document.getElementById("form-holder"),
          "new",
        )
      ) {
        _parent.showMeetingBackButtonPopup();
      } else {
        _parent.showMeetingsScreen();
      }
      return;
    }

    const assetId =
      SalesToolkit.Pages.CurrentPage.getQueryParam("fromAsset") || "";
    if (assetId) {
      const urlParams = _getUrlVars();
      delete urlParams.fromAsset;
      // Navigate back to the asset screen
      _parent.navigator.loadAssetScreen(
        assetId,
        new URLSearchParams(urlParams).toString(),
      );
      return;
    }

    const breadcrumbs = document.querySelectorAll(".breadsmall");
    if (breadcrumbs.length <= 0) {
      _parent.navigator.loadIndexScreen();
    } else if (breadcrumbs.length == 1) {
      breadcrumbs[0].click();
    } else {
      // Click the second-to-last breadcrumb to navigate "up" one level
      breadcrumbs[breadcrumbs.length - 2].click();
    }
  }

  /**
   * Generates a universally unique identifier.
   * @returns {string} The UUID.
   */
  function _uuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        let r = (Math.random() * 16) | 0,
          v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  }

  function _handleLoadForm() {
    const newFormId = event.detail.formId;
    const newForm = SalesToolkit.Forms.formList[newFormId];

    if (!newForm) {
      console.error("Form ID not found in SalesToolkit.Forms.formList");
      return;
    }

    _form = newForm;
    _meetingId = null;
    let collectionId = _form.collectionId || null;

    if (collectionId) {
      SalesToolkit.Theme.setOverridingCollection(collectionId);
    }

    const url = new URL(window.location);
    url.searchParams.set("formId", newFormId);
    if (collectionId) url.searchParams.set("collectionId", collectionId);
    window.history.replaceState({}, "", url);
    _initForm(_form, collectionId);
  }

   /**
   * Handler for when the QR Code modal is fully hidden.
   * Resets submission state and cancels remote completion if active.
   */
  function _onQrCodeModalHidden() {
    _submitting = false;

    if (_remoteCompletionWaiting) {
      _checkRemoteCompletionEnd(false);
    }
  }

  //#endregion

  /**
   * Initializes the preview form module.
   * @param {object} parent - The parent application controller.
   */
  function _init(parent) {
    _parent = parent;
    const stk = parent.getSalesToolkit();
    if (stk) {
      SalesToolkit = stk;
    }
    console.log("Initializing previewFormModule...");

    _parent.loadContacts();
    const wrapper = document.getElementById("previewFormWrapper");
    if (wrapper) {
      wrapper.addEventListener("click", _handleActionClick);
    }

    const formQRCode = document.getElementById("formQRCode");
    if (formQRCode) {
      const padding = SalesToolkit.Theme.getIntegerProperty(
        "formQRCodePadding",
        50,
      );
      formQRCode.style.padding = `${padding}px`;
    }

    const formId = SalesToolkit.Pages.CurrentPage.getQueryParam("formId");
    _meetingId = SalesToolkit.Pages.CurrentPage.getQueryParam("meetingId");
    const collectionId =
      SalesToolkit.Pages.CurrentPage.getQueryParam("collectionId");

    if (collectionId) {
      SalesToolkit.Theme.setOverridingCollection(collectionId);
    }

    if (_meetingId) {
      window.meetingId = _meetingId;
    }

    _form = SalesToolkit.Forms.formList[formId];

    if (_form) {
      _initForm(_form, collectionId);
    }

    _initTreeView();

    _currentWidth = window.innerWidth;
    _currentHeight = window.innerHeight;

    
    const qrCodeModal = document.getElementById("qrCodeModal");
    if (qrCodeModal) {
      _parent.addModalListener("qrCodeModal", "hidden.bs.modal", _onQrCodeModalHidden);
    }

    window.addEventListener("resize", _resizeHandler);

    _eventHandlerMap = {
      [_parent.Events.LOAD_FORM_WITH_ID]: _handleLoadForm,
      [_parent.Events.SIGNATURE_PAD_INITIALIZED]: _handleSigPadInit,
      [_parent.Events.FORM_API_CALL_STATUS_CHANGED]: _handleApiCallStatusChange,
      [_parent.Events.REQUEST_PENDING_SUBMIT]: _executePendingSubmit,
      [_parent.Events.TOGGLE_TREEVIEW]: _toggleTree,
      [_parent.Events.REQUEST_FORM_SUBMIT]: _sendForm,
      [_parent.Events.REQUEST_FORM_SHARE_LINK]: _shareLink,
    };

    for (const eventName in _eventHandlerMap) {
      if (Object.hasOwn(_eventHandlerMap, eventName)) {
        document.addEventListener(eventName, _eventHandlerMap[eventName]);
      }
    }

    SalesToolkit.Cache.onSyncFinished = function () {
      if (_treeView) {
        _treeView.populateTree();
        _treeView.refresh();
      }
      SalesToolkit.UI.Cart.updateMenuCartBadge();
    };

    // Initialize layout column variables from theme
    _treeViewLargeCols =
      SalesToolkit.Theme.getIntegerProperty("treeViewPanelLargeCols", 2) +
      SalesToolkit.Theme.getIntegerProperty("treeViewExtraSpaceLargeCols", 1);
    _treeViewMediumCols =
      SalesToolkit.Theme.getIntegerProperty("treeViewPanelMediumCols", 3) +
      SalesToolkit.Theme.getIntegerProperty("treeViewExtraSpaceMediumCols", 1); // Corrected from LargeCols
    _treeViewSmallCols =
      SalesToolkit.Theme.getIntegerProperty("treeViewPanelSmallCols", 0) +
      SalesToolkit.Theme.getIntegerProperty("treeViewExtraSpaceSmallCols", 0);

    _setupColumns();

    // Check if the tree should be hidden on initial load
    if (localStorage.getItem("hideTree")) {
      _toggleTree();
    }

    // Toggle camera icon visibility based on theme settings
    const cameraButton = document.getElementById("salestoolkit-camera");
    if (cameraButton) {
      cameraButton.style.display = SalesToolkit.Theme.getBooleanProperty(
        "showFormCameraIcon",
        false,
      )
        ? "inline-block"
        : "none";
    }

    // Initialize camera and QR code reader instances
    const cameraFeed = document.getElementById("cameraFeedVideo");
    if (cameraFeed) {
      _cardReader = new CardReader(cameraFeed);
    }
    _html5QrCode = new Html5Qrcode("barqrcode");

    const formHolder = document.getElementById("form-holder");
    if (formHolder) {
      formHolder.addEventListener("blur", _handleFormInputBlur, true);
    }

    _resizeHandler();

    _setupGLU();

    // -- Form History Initializers --
    setTimeout(function () {
      _loadFromFormHistory();
      _setupLongTouchListener();

      const formHistory = SalesToolkit.Summary.shares.filter(
        (x) =>
          x.type === "forms" && x.userId === SalesToolkit.User.id && x.editable,
      );
      if (
        SalesToolkit.Theme.getBooleanProperty(
          "showFormHistoryDropdown",
          false,
        ) &&
        _form?.allowSubmissionUpdates &&
        formHistory.length > 0
      ) {
        _buildFormHistoryDropdown();
      }
    }, 300);

    _updateContacts();

    _setupCompatibilityLayer();
  }

  function _setupCompatibilityLayer() {
    window.sendForm = _sendForm;
    window.shareLink = _shareLink;
  }

  function _removeCompatibilityLayer() {
    delete window.sendForm;
    delete window.shareLink;
  }

  /**
   * Destroys the module, cleaning up event listeners and references.
   */
  function _destroy() {
    console.log("Destroying previewFormModule...");
    if (_treeView && typeof _treeView.destroy === "function") {
      _treeView.destroy();
    }
    _treeView = null;

    const wrapper = document.getElementById("previewFormWrapper");
    if (wrapper) {
      wrapper.removeEventListener("click", _handleActionClick);
    }

    _pendingSubmit = null;

    if (window.meetingId) {
      try {
        delete window.meetingId;
      } catch (e) {
        console.log(e);
        window.meetingId = undefined; // Fallback
      }
    }
    _meetingId = null;

    const qrCodeModal = document.getElementById("qrCodeModal");
    if (qrCodeModal) {
      _parent.removeModalListener("qrCodeModal", "hidden.bs.modal", _onQrCodeModalHidden);
    }

    window.removeEventListener("resize", _resizeHandler);
    clearTimeout(_viewFormResizeTimeout);

    if (autoScanTimeout) {
      clearTimeout(autoScanTimeout);
      autoScanTimeout = null;
    }
    if (_pollingIntervalId) {
      clearInterval(_pollingIntervalId);
      _pollingIntervalId = null;
    }

    if (_countdownIntervalId) {
      clearInterval(_countdownIntervalId);
      _countdownIntervalId = null;
    }

    if (_mainTimeoutId) {
      clearTimeout(_mainTimeoutId);
      _mainTimeoutId = null;
    }

    if (_countdownStarterId) {
      clearTimeout(_countdownStarterId);
      _countdownStarterId = null;
    }

    if (_eventHandlerMap) {
      for (const eventName in _eventHandlerMap) {
        if (Object.hasOwn(_eventHandlerMap, eventName)) {
          document.removeEventListener(eventName, _eventHandlerMap[eventName]);
        }
      }
      // Clean up the map itself to be tidy.
      _eventHandlerMap = null;
    }

    const formHolder = document.getElementById("form-holder");
    if (formHolder) {
      formHolder.removeEventListener("blur", _handleFormInputBlur, true);
    }

    _removeCompatibilityLayer();
  }

  /**
   * The HTML template for the module's view.
   */
  const _templateHTML = `
     <div id="previewFormWrapper" class="page-content-wrapper">
     <!-- BEGIN QR Code Modal -->
     <div class="modal fade" id="qrCodeModal" tabindex="-1" role="dialog" aria-labelledby="qrCodeModalLabel" aria-hidden="true">
        <div class="modal-dialog" style="width:fit-content;" role="document">
            <div class="modal-content">
                <div class="portlet-title modal-header">
                    <h5 class="modal-title" id="qrCodeModalLabel"><strong><span id="qrCodeModalTitle" data-translation-key="qrCodeModalTitle" data-translation-default="Information in QR form">Information in QR Form</span></strong></h5>
                </div>
                <div class="portlet-title modal-body">
                    <div id="formQRCode"></div>
                    <span id="externalQRCode"></span>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn default btn-secondary" data-dismiss="modal"><span data-translation-key="close" data-translation-default="Close">Close</span></button>
                </div>
            </div>
        </div>
    </div>
    <!-- END QR Code Modal -->
     <!-- BEGIN SCAN RESULTS Modal -->
    <div class="modal fade" id="contactFilterModal" tabindex="-1" role="dialog" aria-labelledby="contactFilterModalLabel"
        aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="contactFilterModalLabel" data-translation-key="filterContacts"
                        data-translation-default="Filter Contacts">Filter Contacts</h5>
                </div>
                <div class="portlet-title modal-body">
                    <div id="contactFilterOptions"></div>
                    <div id="externalAPITimer" stlye="text-align:right; display:none"></div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn default btn-secondary" data-dismiss="modal"><span data-translation-key="cancelButton"
                            data-translation-default="Cancel">Cancel</span></button>
                            <button id="restartButton" type="button" class="btn blue btn-primary" data-action="filtersStartOver"><span data-translation-key="startOver"
                                data-translation-default="Restart">Restart</span></button>
                </div>
            </div>
        </div>
    </div>
    <!-- END SCAN RESULTS MODAL-->
     <!-- BEGIN Camera Modal -->
    <div class="modal fade" id="cameraModal" tabindex="-1" role="dialog" aria-labelledby="cameraModalLabel"
        aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="cameraModalLabel" data-translation-key="scanCard"
                        data-translation-default="Scan Card">Scan Card</h5>
                </div>
                <div class="portlet-title modal-body">
                    <div id="scanAreaDark" style="display:none; overflow:hidden; position: absolute; display: flex; justify-content: center; align-items: center;">
                        <div id="scanAreaLight" style="box-shadow: 0 0 0 5000px rgb(0 0 0 / 0.3); border-radius: 20px;"></div>
                    </div>
                    <span id="scan-explain" style="text-align: center; width: 100%; display: block; margin-bottom: 10px;" data-translation-key="scanCardExplanation"
                          data-translation-default="Scan your business card or badge below. For badges please center within the crop area.">Scan your business card or badge below. For badges please center within the crop area.</span>
                    <video style="display: block; margin: 0 auto; max-width: 100%; " playsinline autoplay id="cameraFeedVideo"></video>
                    <span id="scan-info" style="text-align: center; width: 100%; display: none; margin-bottom: 10px;"></span>
                    <div style="height:0; width:0; display:none;" id="barqrcode"></div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn default btn-secondary" data-dismiss="modal"
                        data-action="stopCamera"><span data-translation-key="cancelButton"
                            data-translation-default="Cancel">Cancel</span></button>
                    <button type="button" class="btn salestoolkit-blue btn-primary" data-action="takeScan"><span
                            data-translation-key="takePicture" data-translation-default="Take Picture">Take
                            Picture</span></button>
                </div>
            </div>
        </div>
    </div>
    <!-- END CAMERA MODAL-->
     <!-- Start Clear form modal Modal -->
    <div class="modal fade" id="clearFormModal" tabindex="-1" role="dialog" aria-labelledby="clearFormModalLabel" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="portlet-title modal-header">
                    <h5 class="modal-title" id="clearFormModalLabel"><strong><span id="clearFormTitle" data-translation-key="clearFormTitle" data-translation-default="Clearing form">Clearing form</span></strong></h5>
                </div>
                <div class="portlet-title modal-body">
                    <span id="clearFormMessage" data-translation-key="clearFormMessage" data-translation-default="This will remove all information entered in the form, are you sure you wish to proceed?">This will remove all information entered in the form, are you sure you wish to proceed?</span>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn default btn-secondary" data-dismiss="modal"><span data-translation-key="cancelButton" data-translation-default="Cancel">Cancel</span></button>
                    <button data-action="clearForm" type="button" class="btn salestoolkit-blue btn-primary" data-dismiss="modal"><span data-translation-key="clear" data-translation-default="Clear">Clear</span></button>
                </div>
            </div>
        </div>
    </div>
    <!-- END CLear Modal-->
     <!-- BEGIN Tncs Modal -->
    <div class="modal fade" id="tncsModal" tabindex="-1" role="dialog" aria-labelledby="tncsModalLabel" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="portlet-title modal-header">
                    <h5 class="modal-title" id="tncsModalLabel"><strong><span id="tncsTitle" data-translation-key="tncsTitle" data-translation-default="Terms and Conditions">Terms and Conditions</span></strong></h5>
                </div>
                <div class="portlet-title modal-body">
                    <span id="legalHtml"></span>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn default btn-secondary" data-dismiss="modal"><span data-translation-key="close" data-translation-default="Close">Close</span></button>
                </div>
            </div>
        </div>
    </div>
    <!-- END Tncs Modal -->
    <!-- BEGIN Content -->
    <div id="main-div-center" class="page-content view-asset view-form" style="padding-bottom:0px">
                <!-- BEGIN PAGE HEADER-->

                <div class="page-title">
                    <ul class="page-breadcrumb" id="salestoolkit-breadcrumbs" style="white-space: nowrap; display: flex; max-width: 100%; width: 100%;">

                        <li>
                            <a class="breadsmall" href="#"></a>
                        </li>

                    </ul>

                </div>


                <!-- END PAGE HEADER-->
                
                <!-- BEGIN PAGE CONTENT-->
                <div class="row">
                    <div id="main-panel" class="col-md-9 salestoolkit-main-panel">
                        <div class="sidePadding10 portlet light view-asset view-form">
                            <div class="portlet-title">
                                <div class="caption">
                                    <!-- <i class="fa fa-desktop font-blue"></i> -->
                                    <strong><span id="salestoolkit-form-name"> </span></strong>
                                </div>

                                <div class="actions">

                                    <div id="formHistoryDropdown" style="display:none;"></div>

                                    <a style="display: none;" class="btn btn-sm" id="salestoolkit-tncs" data-action="showTncsModal" href="javascript:;" 
                                    data-original-title="Terms and Conditions" 
                                    data-tooltip-translationkey="termsAndConditions"
                                    data-tooltip-translation-default="Terms and Conditions">
                                        <img id="form-terms-icon" style="display: inline;">
                                    </a>

                                    <a style="display: none;" class="btn btn-sm" id="salestoolkit-camera" data-action="openedCameraModal" href="javascript:;" 
                                    data-original-title="Scan" 
                                    data-tooltip-translationkey="scan"
                                    data-tooltip-translation-default="Scan">
                                        <img id="form-camera-icon" style="display:inline;">
                                    </a>

                                    <a class="btn btn-sm" style="display:none"  id="salestoolkit-clear" data-action="askToClearForm" href="javascript:;" 
                                    data-original-title="Clear Form" 
                                    data-tooltip-translationkey="clearForm"
                                    data-tooltip-translation-default="Clear Form">
                                        <img id="form-clear-icon" style="display: inline;">
                                    </a>

                                    <a class="btn btn-sm" style="display:none" id="salestoolkit-submit" data-action="sendForm" href="javascript:;" 
                                    data-original-title="Send Form" 
                                    data-tooltip-translationkey="sendForm"
                                    data-tooltip-translation-default="Send Form">
                                        <img id="form-send-icon" style="display: inline;">
                                    </a>

                                    <a class="btn btn-sm" style="display:none" id="salestoolkit-qrsubmit" data-action="qrSubmit" href="javascript:;" 
                                    data-original-title="Create QR" 
                                    data-tooltip-translationkey="createQR"
                                    data-tooltip-translation-default="Create QR">
                                        <img id="form-qr-icon" style="display: inline;">
                                    </a>

                                    <a style="display:none;" class="btn btn-sm" id="salestoolkit-sharelink" data-action="shareLink" href="javascript:;"
                                    data-original-title="Share" 
                                    data-tooltip-translationkey="shareLink"
                                    data-tooltip-translation-default="Share">
                                        <img id="form-share-link-icon" style="display: inline;">
                                    </a>

                                    <a class="btn btn-circle btn-icon-only btn-default fullscreen" data-action="fullscreen"
                                       href="javascript:;">
                                        <img class="zoomin-icon"
                                        data-original-title="Full Screen"
                                       data-tooltip-translationkey="fullscreen"
                                       data-tooltip-translation-default="Full Screen">

                                        <img class="zoomout-icon"
                                        data-original-title="Minimise"
                                       data-tooltip-translationkey="minimise"
                                       data-tooltip-translation-default="Minimise">
                                    </a>
                                </div>


                            </div>

                            <div class="portlet-body view-asset view-form">

                                <div id="breadcrumbsbuttonhide" class="breadcrumbsBackButton">

                                    <a type="button" data-action="backButtonHome">
                                        <img class="backButtonIcon" src="img/default/back_icon.svg" width="20px" height="20px">
                                        <script>
                                            applyThemeToImage('salestoolkitPortletBackButtonIcon', 'img.backButtonIcon', 'img/default/back_icon.svg');
                                        </script>
                                        <span style="font-weight: 600; font-size: 14px; margin-left: 4px;"
                                              data-translation-key="back" data-translation-default="Back">Back</span>
                                    </a>
                                </div>


                                <div class="row">
                                    <div class="col-sm-12" style="overflow: hidden; padding: 0 15px;">
                                        <!--<iframe id="the_iframe" scrolling="NO" height="768" frameBorder="0" style="-ms-transform-origin: 0 0; transform-origin: 0px 0px 0px;"></iframe>-->
                                        <form id="form-holder" autocomplete="off"></form>

                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                    <div id="tree-view-panel" class="col-md-3" style="display: block;">
                        <div class="portlet light view-asset view-form ">
                            <div class="portlet-title">
                                <div class="caption">
                                    <!-- <i class="fa fa-sitemap font-blue"></i> -->
                                    <strong>
                                        <span class='hasTranslationKey'
                                              data-translation-key="treeView"
                                              data-translation-default="Tree View">Tree View</span>
                                    </strong>
                                </div>
                                <div class="actions">
                                    <a href="javascript:;" data-action="fullscreen"
                                       class="btn btn-circle btn-icon-only btn-default fullscreen">
                                        <img class="zoomin-icon" src="img/default/zoom_icon.svg"
                                        data-original-title="Full Screen"
                                       data-tooltip-translationkey="fullscreen"
                                       data-tooltip-translation-default="Full Screen">

                                        <img class="zoomout-icon" src="img/default/zoom_out_icon.svg"
                                        data-original-title="Minimise"
                                       data-tooltip-translationkey="minimise"
                                       data-tooltip-translation-default="Minimise">
                                    </a>
                                </div>
                            </div>
                            <div class="portlet-body view-asset view-form">
                                <div id="atheneum-tree-view"></div>
                            </div>

                        </div>
                    </div>
                </div>
                 <!-- END PAGE CONTENT-->
            </div>
           <!-- END CONTENT -->
    </div>
  `;

  // Expose public methods and properties
  return {
    init: _init,
    destroy: _destroy,
    templateHTML: _templateHTML,
  };
})();
